// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import axios, { AxiosResponse } from 'axios';
import { AccountList, DeleteAccountRequest } from '../types/accounts';
import { StorageObjectList, StorageSearchFilters, StorageObjectRequest, DeleteStorageObjectRequest } from '../types/storage';
import { RpcFunctionList, RpcCallRequest, RpcCallResponse, ApiExplorerRequest, ApiExplorerResponse } from '../types/rpc';
import { Leaderboard, LeaderboardList, LeaderboardRecordList, LeaderboardSearchFilters, LeaderboardRecordSearchFilters, CreateLeaderboardRequest, DeleteLeaderboardRequest, WriteLeaderboardRecordRequest, DeleteLeaderboardRecordRequest } from '../types/leaderboards';
import { GroupList, GroupUserList, GroupSearchFilters, GroupUserSearchFilters, CreateGroupRequest, UpdateGroupRequest, DeleteGroupRequest, AddGroupUsersRequest, RemoveGroupUsersRequest, PromoteGroupUsersRequest, DemoteGroupUsersRequest, BanGroupUsersRequest, KickGroupUsersRequest } from '../types/groups';
import { ChatMessageList, ChatChannelList, ChatMessageSearchFilters, ChatChannelSearchFilters, DeleteChatMessageRequest, UpdateChatMessageRequest, SendChatMessageRequest } from '../types/chatMessages';
import { NotificationList, NotificationTemplateList, NotificationSearchFilters, SendNotificationRequest, SendNotificationBatchRequest, DeleteNotificationRequest, DeleteNotificationBatchRequest, CreateNotificationTemplateRequest, UpdateNotificationTemplateRequest, DeleteNotificationTemplateRequest, NotificationStats } from '../types/notifications';
import { UserList, UserSearchFilters, CreateUserRequest, UpdateUserRequest, DeleteUserRequest, BanUserRequest, UnbanUserRequest } from '../types/users';
import { Config, UpdateConfigRequest } from '../types/config';
import { RuntimeModuleList, RuntimeInfo } from '../types/modules';
import { PurchaseList, PurchaseSearchFilters, ValidatePurchaseRequest } from '../types/purchases';
import { SubscriptionList, SubscriptionSearchFilters } from '../types/subscriptions';
import { MatchList, MatchSearchFilters } from '../types/matches';
import { 
  AnalyticsFilters, 
  AnalyticsResponse, 
  DashboardOverview, 
  CoinMetrics, 
  AdMetrics, 
  LeaderboardMetrics, 
  QuizMetrics, 
  GameModeMetrics,
  ExportOptions,
  ExportResponse
} from '../types/analytics';
import {
  ContainerConfig,
  ContainerConfigRequest,
  ContainerConfigResponse,
  ContainerStatsResponse,
  ContainerLogsRequest,
  ContainerLogsResponse,
  ContainerOperationRequest,
  ContainerOperationResponse
} from '../types/containerSettings';
import { AdManagementDashboard, AdNetworkConfig, CreateAdNetworkRequest, UpdateAdNetworkRequest, AdNetworkTestResult, Platform, AdType } from '../types/adManagement';
import { CoinManagementDashboard, CreateCoinPackageRequest, ManualAdjustment, BonusType, TransactionType, TransactionSource } from '../types/coinManagement';
import { QuizManagementDashboard, CreateQuestionRequest, UpdateQuestionRequest, DeleteQuestionRequest, QuestionCategory, QuestionDifficulty, QuestionType, QuestionStatus, QuestionCategoryInfo, CreateQuestionCategoryRequest, UpdateQuestionCategoryRequest, DeleteQuestionCategoryRequest, BackendQuizCategoriesResponse, BackendQuizCategory, deserializeDifficulty, AddQuizCategoryRequest, BackendDifficulty, RemoveQuizCategoryRequest, AddQuizQuestionToCategoryRequest, RemoveQuizQuestionFromCategoryRequest, GetQuizQuestionsByCategoryRequest, QuizQuestionByCategoryResponse } from '../types/quizManagement';
import { DashboardOverview as MainDashboardOverview, AddQuestionRequest, SendNotificationRequest as DashboardSendNotificationRequest, BanUserRequest as DashboardBanUserRequest, GameTitle, GameMode } from '../types/dashboard';
import { prometheusService } from './prometheusService';
import { authService } from './authService';

class ConsoleService {
  private config = {
    host: process.env.REACT_APP_API_HOST || 'http://localhost:7351'
  };

  // In-memory mock store for quiz categories (used when backend is not available)
  private quizCategoriesMock: QuestionCategoryInfo[] | null = null;

  private getAuthHeaders() {
    const session = authService.getSession();
    if (!session?.token) {
      throw new Error('No authentication token available');
    }
    return {
      Authorization: `Bearer ${session.token}`
    };
  }

  private toBackendQuizCorrectOptionIndex(correctOptionIndex: number): number {
    // Keep the admin UI/import flow user-friendly with 1-4, but persist the
    // value in the zero-based format expected by the quiz runtime.
    if (!Number.isFinite(correctOptionIndex)) {
      return correctOptionIndex;
    }

    if (correctOptionIndex >= 1 && correctOptionIndex <= 4) {
      return correctOptionIndex - 1;
    }

    return correctOptionIndex;
  }

  public async listAccounts(
    serverKey: string,
    filter?: string,
    tombstones?: boolean,
    cursor?: string | null
  ): Promise<AccountList> {
    try {
      const params = new URLSearchParams();
      
      if (filter) {
        params.append('filter', filter);
      }
      if (tombstones) {
        params.append('tombstones', 'true');
      }
      if (cursor) {
        params.append('cursor', cursor);
      }

      const response = await axios.get<AccountList>(
        `${this.config.host}/v2/console/account`,
        {
          headers: this.getAuthHeaders(),
          params: params
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to list accounts:', error);
      throw error;
    }
  }

  public async deleteAccount(
    serverKey: string,
    userId: string,
    recordDeletion: boolean
  ): Promise<void> {
    try {
      const payload: DeleteAccountRequest = {
        user_id: userId,
        record_deletion: recordDeletion
      };

      await axios.delete(
        `${this.config.host}/v2/console/account/${userId}`,
        {
          headers: this.getAuthHeaders(),
          data: payload
        }
      );
    } catch (error) {
      console.error('Failed to delete account:', error);
      throw error;
    }
  }

  public async getAccount(userId: string): Promise<any> {
    try {
      const response = await axios.get(
        `${this.config.host}/v2/console/account/${userId}`,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to get account:', error);
      throw error;
    }
  }

  public async updateAccount(serverKey: string, userId: string, updateRequest: any): Promise<any> {
    try {
      const response = await axios.post(
        `${this.config.host}/v2/console/account/${userId}`,
        updateRequest,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to update account:', error);
      throw error;
    }
  }

  public async updateAccountWallet(userId: string, wallet: Record<string, number>): Promise<any> {
    try {
      // Use the Nakama Console API to update wallet via account update endpoint
      // Wallet must be sent as a JSON string
      const response = await axios.post(
        `${this.config.host}/v2/console/account/${userId}`,
        { wallet: JSON.stringify(wallet) },
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to update account wallet:', error);
      throw error;
    }
  }

  public async banAccount(serverKey: string, userId: string): Promise<void> {
    try {
      await axios.post(
        `${this.config.host}/v2/console/account/${userId}/ban`,
        {},
        {
          headers: this.getAuthHeaders()
        }
      );
    } catch (error) {
      console.error('Failed to ban account:', error);
      throw error;
    }
  }

  public async unbanAccount(serverKey: string, userId: string): Promise<void> {
    try {
      await axios.post(
        `${this.config.host}/v2/console/account/${userId}/unban`,
        {},
        {
          headers: this.getAuthHeaders()
        }
      );
    } catch (error) {
      console.error('Failed to unban account:', error);
      throw error;
    }
  }

  public async exportAccount(serverKey: string, userId: string): Promise<any> {
    try {
      const response = await axios.get(
        `${this.config.host}/v2/console/account/${userId}/export`,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to export account:', error);
      throw error;
    }
  }

  public async getAccountNotifications(userId: string, cursor?: string | null): Promise<NotificationList> {
    try {
      const params = new URLSearchParams();
      if (cursor) {
        params.append('cursor', cursor);
      }

      const response = await axios.get<NotificationList>(
        `${this.config.host}/v2/console/account/${userId}/notification`,
        {
          headers: this.getAuthHeaders(),
          params: params
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to get account notifications:', error);
      throw error;
    }
  }

  public async getStatus(serverKey: string): Promise<any> {
    try {
      const response = await axios.get(
        `${this.config.host}/v2/console/status`,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to get status:', error);
      throw error;
    }
  }

  // Storage methods
  public async listStorageObjects(
    serverKey: string,
    collection?: string,
    key?: string,
    userId?: string,
    cursor?: string | null
  ): Promise<StorageObjectList> {
    try {
      const params = new URLSearchParams();
      
      if (collection) {
        params.append('collection', collection);
      }
      if (key) {
        params.append('key', key);
      }
      if (userId) {
        params.append('user_id', userId);
      }
      if (cursor) {
        params.append('cursor', cursor);
      }

      const response = await axios.get<StorageObjectList>(
        `${this.config.host}/v2/console/storage`,
        {
          headers: this.getAuthHeaders(),
          params: params
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to list storage objects:', error);
      throw error;
    }
  }

  public async getStorageObject(
    serverKey: string,
    collection: string,
    key: string,
    userId: string
  ): Promise<any> {
    try {
      const response = await axios.get(
        `${this.config.host}/v2/console/storage/${collection}/${key}/${userId}`,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to get storage object:', error);
      throw error;
    }
  }

  public async writeStorageObject(
    serverKey: string,
    request: StorageObjectRequest
  ): Promise<any> {
    try {
      const collection = encodeURIComponent(request.collection);
      const key = encodeURIComponent(request.key);
      const userId = encodeURIComponent(request.user_id);

      const response = await axios.put(
        `${this.config.host}/v2/console/storage/${collection}/${key}/${userId}`,
        request,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to write storage object:', error);
      throw error;
    }
  }

  public async deleteStorageObjects(
    serverKey: string,
    request: DeleteStorageObjectRequest
  ): Promise<void> {
    try {
      await axios.delete(
        `${this.config.host}/v2/console/storage`,
        {
          headers: this.getAuthHeaders(),
          data: request
        }
      );
    } catch (error) {
      console.error('Failed to delete storage objects:', error);
      throw error;
    }
  }

  // RPC methods
  public async listRpcFunctions(serverKey: string): Promise<RpcFunctionList> {
    try {
      const response = await axios.get<RpcFunctionList>(
        `${this.config.host}/v2/console/runtime`,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to list RPC functions:', error);
      throw error;
    }
  }

  public async callRpcFunction(
    request: RpcCallRequest
  ): Promise<RpcCallResponse> {
    try {
      const response = await axios.post<RpcCallResponse>(
        `${this.config.host}/v2/console/api/endpoints/rpc/${request.id}`,
        {
          body: request.body,
          user_id: request.user_id,
          http_key: request.http_key
        },
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to call RPC function:', error);
      throw error;
    }
  }

  // API Explorer methods
  public async makeApiRequest(
    request: ApiExplorerRequest
  ): Promise<ApiExplorerResponse> {
    const startTime = Date.now();
    
    try {
      const config: any = {
        method: request.method.toLowerCase(),
        url: `${this.config.host}${request.path}`,
        headers: {
          ...this.getAuthHeaders(),
          ...request.headers
        },
        params: request.query_params,
        validateStatus: () => true // Don't throw on HTTP error status codes
      };

      if (request.body && ['post', 'put', 'patch'].includes(request.method.toLowerCase())) {
        try {
          config.data = JSON.parse(request.body);
          config.headers['Content-Type'] = 'application/json';
        } catch {
          config.data = request.body;
          config.headers['Content-Type'] = 'text/plain';
        }
      }

      const response = await axios(config);
      const duration = Date.now() - startTime;

      return {
        status: response.status,
        status_text: response.statusText,
        headers: response.headers as Record<string, string>,
        body: typeof response.data === 'string' ? response.data : JSON.stringify(response.data, null, 2),
        duration
      };
    } catch (error: any) {
      const duration = Date.now() - startTime;
      
      if (error.response) {
        return {
          status: error.response.status,
          status_text: error.response.statusText,
          headers: error.response.headers as Record<string, string>,
          body: typeof error.response.data === 'string' ? error.response.data : JSON.stringify(error.response.data, null, 2),
          duration
        };
      }

      return {
        status: 0,
        status_text: 'Network Error',
        headers: {},
        body: error.message || 'Request failed',
        duration
      };
    }
  }

  public async getApiEndpoints(serverKey: string): Promise<any> {
    try {
      const response = await axios.get(
        `${this.config.host}/v2/console/api`,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to get API endpoints:', error);
      throw error;
    }
  }

  // Leaderboard methods
  public async listLeaderboards(
    serverKey: string,
    filters?: LeaderboardSearchFilters,
    cursor?: string | null
  ): Promise<LeaderboardList> {
    try {
      const params = new URLSearchParams();
      
      if (filters?.leaderboard_id) {
        params.append('leaderboard_id', filters.leaderboard_id);
      }
      if (filters?.category !== undefined) {
        params.append('category', filters.category.toString());
      }
      if (filters?.authoritative !== undefined) {
        params.append('authoritative', filters.authoritative.toString());
      }
      if (cursor) {
        params.append('cursor', cursor);
      }

      const response = await axios.get<LeaderboardList>(
        `${this.config.host}/v2/console/leaderboard`,
        {
          headers: this.getAuthHeaders(),
          params: params
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to list leaderboards:', error);
      throw error;
    }
  }

  public async getLeaderboard(
    serverKey: string,
    leaderboardId: string
  ): Promise<any> {
    try {
      const response = await axios.get(
        `${this.config.host}/v2/console/leaderboard/${leaderboardId}`,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to get leaderboard:', error);
      throw error;
    }
  }

  public async createLeaderboard(
    serverKey: string,
    request: CreateLeaderboardRequest
  ): Promise<any> {
    try {
      const response = await axios.post(
        `${this.config.host}/v2/console/leaderboard`,
        request,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to create leaderboard:', error);
      throw error;
    }
  }

  public async deleteLeaderboard(
    serverKey: string,
    leaderboardId: string
  ): Promise<void> {
    try {
      await axios.delete(
        `${this.config.host}/v2/console/leaderboard/${leaderboardId}`,
        {
          headers: this.getAuthHeaders()
        }
      );
    } catch (error) {
      console.error('Failed to delete leaderboard:', error);
      throw error;
    }
  }

  public async listLeaderboardRecords(
    serverKey: string,
    leaderboardId: string,
    filters?: LeaderboardRecordSearchFilters
  ): Promise<LeaderboardRecordList> {
    try {
      const params = new URLSearchParams();
      
      if (filters?.owner_ids && filters.owner_ids.length > 0) {
        filters.owner_ids.forEach(id => params.append('owner_ids', id));
      }
      if (filters?.limit) {
        params.append('limit', filters.limit.toString());
      }
      if (filters?.cursor) {
        params.append('cursor', filters.cursor);
      }
      if (filters?.expiry) {
        params.append('expiry', filters.expiry);
      }

      const response = await axios.get<LeaderboardRecordList>(
        `${this.config.host}/v2/console/leaderboard/${leaderboardId}/record`,
        {
          headers: this.getAuthHeaders(),
          params: params
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to list leaderboard records:', error);
      throw error;
    }
  }

  public async writeLeaderboardRecord(
    serverKey: string,
    request: WriteLeaderboardRecordRequest
  ): Promise<any> {
    try {
      const response = await axios.put(
        `${this.config.host}/v2/console/leaderboard/${request.leaderboard_id}/record`,
        request.record,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to write leaderboard record:', error);
      throw error;
    }
  }

  public async deleteLeaderboardRecord(
    serverKey: string,
    leaderboardId: string,
    ownerId: string
  ): Promise<void> {
    try {
      // Use Nakama Console API to delete leaderboard record
      const response = await axios.delete(
        `${this.config.host}/v2/console/leaderboard/${leaderboardId}/owner/${ownerId}`,
        {
          headers: this.getAuthHeaders()
        }
      );
      console.log('Delete leaderboard record response:', response.status, response.data);
    } catch (error: any) {
      console.error('Failed to delete leaderboard record:', error);
      console.error('Response:', error.response?.status, error.response?.data);
      throw error;
    }
  }

  // Groups methods
  public async listGroups(
    serverKey: string,
    filters?: GroupSearchFilters,
    cursor?: string | null
  ): Promise<GroupList> {
    try {
      const params = new URLSearchParams();
      
      if (filters?.name) {
        params.append('name', filters.name);
      }
      if (filters?.lang_tag) {
        params.append('lang_tag', filters.lang_tag);
      }
      if (filters?.members !== undefined) {
        params.append('members', filters.members.toString());
      }
      if (filters?.open !== undefined) {
        params.append('open', filters.open.toString());
      }
      if (cursor) {
        params.append('cursor', cursor);
      }

      const response = await axios.get<GroupList>(
        `${this.config.host}/v2/console/group`,
        {
          headers: this.getAuthHeaders(),
          params: params
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to list groups:', error);
      throw error;
    }
  }

  public async getGroup(
    serverKey: string,
    groupId: string
  ): Promise<any> {
    try {
      const response = await axios.get(
        `${this.config.host}/v2/console/group/${groupId}`,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to get group:', error);
      throw error;
    }
  }

  public async createGroup(
    serverKey: string,
    request: CreateGroupRequest
  ): Promise<any> {
    try {
      const response = await axios.post(
        `${this.config.host}/v2/console/group`,
        request,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to create group:', error);
      throw error;
    }
  }

  public async updateGroup(
    serverKey: string,
    request: UpdateGroupRequest
  ): Promise<any> {
    try {
      const response = await axios.put(
        `${this.config.host}/v2/console/group/${request.group_id}`,
        request,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to update group:', error);
      throw error;
    }
  }

  public async deleteGroup(
    serverKey: string,
    groupId: string
  ): Promise<void> {
    try {
      await axios.delete(
        `${this.config.host}/v2/console/group/${groupId}`,
        {
          headers: this.getAuthHeaders()
        }
      );
    } catch (error) {
      console.error('Failed to delete group:', error);
      throw error;
    }
  }

  public async listGroupUsers(
    serverKey: string,
    groupId: string,
    filters?: GroupUserSearchFilters
  ): Promise<GroupUserList> {
    try {
      const params = new URLSearchParams();
      
      if (filters?.state !== undefined) {
        params.append('state', filters.state.toString());
      }
      if (filters?.cursor) {
        params.append('cursor', filters.cursor);
      }

      const response = await axios.get<GroupUserList>(
        `${this.config.host}/v2/console/group/${groupId}/user`,
        {
          headers: this.getAuthHeaders(),
          params: params
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to list group users:', error);
      throw error;
    }
  }

  public async addGroupUsers(
    serverKey: string,
    request: AddGroupUsersRequest
  ): Promise<void> {
    try {
      await axios.post(
        `${this.config.host}/v2/console/group/${request.group_id}/user`,
        { user_ids: request.user_ids },
        {
          headers: this.getAuthHeaders()
        }
      );
    } catch (error) {
      console.error('Failed to add group users:', error);
      throw error;
    }
  }

  public async removeGroupUsers(
    serverKey: string,
    request: RemoveGroupUsersRequest
  ): Promise<void> {
    try {
      await axios.delete(
        `${this.config.host}/v2/console/group/${request.group_id}/user`,
        {
          headers: this.getAuthHeaders(),
          data: { user_ids: request.user_ids }
        }
      );
    } catch (error) {
      console.error('Failed to remove group users:', error);
      throw error;
    }
  }

  public async promoteGroupUsers(
    serverKey: string,
    request: PromoteGroupUsersRequest
  ): Promise<void> {
    try {
      await axios.post(
        `${this.config.host}/v2/console/group/${request.group_id}/promote`,
        { user_ids: request.user_ids },
        {
          headers: this.getAuthHeaders()
        }
      );
    } catch (error) {
      console.error('Failed to promote group users:', error);
      throw error;
    }
  }

  public async demoteGroupUsers(
    serverKey: string,
    request: DemoteGroupUsersRequest
  ): Promise<void> {
    try {
      await axios.post(
        `${this.config.host}/v2/console/group/${request.group_id}/demote`,
        { user_ids: request.user_ids },
        {
          headers: this.getAuthHeaders()
        }
      );
    } catch (error) {
      console.error('Failed to demote group users:', error);
      throw error;
    }
  }

  public async banGroupUsers(
    serverKey: string,
    request: BanGroupUsersRequest
  ): Promise<void> {
    try {
      await axios.post(
        `${this.config.host}/v2/console/group/${request.group_id}/ban`,
        { user_ids: request.user_ids },
        {
          headers: this.getAuthHeaders()
        }
      );
    } catch (error) {
      console.error('Failed to ban group users:', error);
      throw error;
    }
  }

  public async kickGroupUsers(
    serverKey: string,
    request: KickGroupUsersRequest
  ): Promise<void> {
    try {
      await axios.post(
        `${this.config.host}/v2/console/group/${request.group_id}/kick`,
        { user_ids: request.user_ids },
        {
          headers: this.getAuthHeaders()
        }
      );
    } catch (error) {
      console.error('Failed to kick group users:', error);
      throw error;
    }
  }

  // Chat Messages methods
  public async listChatMessages(
    serverKey: string,
    filters?: ChatMessageSearchFilters,
    cursor?: string | null,
    forward?: boolean
  ): Promise<ChatMessageList> {
    try {
      const params = new URLSearchParams();
      
      if (filters?.channel_id) {
        params.append('channel_id', filters.channel_id);
      }
      if (filters?.sender_id) {
        params.append('sender_id', filters.sender_id);
      }
      if (filters?.username) {
        params.append('username', filters.username);
      }
      if (filters?.content) {
        params.append('content', filters.content);
      }
      if (filters?.room_name) {
        params.append('room_name', filters.room_name);
      }
      if (filters?.group_id) {
        params.append('group_id', filters.group_id);
      }
      if (filters?.code !== undefined) {
        params.append('code', filters.code.toString());
      }
      if (filters?.persistent !== undefined) {
        params.append('persistent', filters.persistent.toString());
      }
      if (filters?.type !== undefined) {
        params.append('type', filters.type.toString());
      }
      if (filters?.label) {
        params.append('label', encodeURIComponent(filters.label));
      }
      if (cursor) {
        params.append('cursor', cursor);
      }
      if (forward !== undefined) {
        params.append('forward', forward.toString());
      }

      const response = await axios.get<ChatMessageList>(
        `${this.config.host}/v2/console/channel`,
        {
          headers: this.getAuthHeaders(),
          params: params
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to list chat messages:', error);
      throw error;
    }
  }

  public async getChatMessage(
    serverKey: string,
    messageId: string,
    channelId: string
  ): Promise<any> {
    try {
      const response = await axios.get(
        `${this.config.host}/v2/console/message/${messageId}`,
        {
          headers: this.getAuthHeaders(),
          params: { channel_id: channelId }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to get chat message:', error);
      throw error;
    }
  }

  public async deleteChatMessage(
    serverKey: string,
    request: DeleteChatMessageRequest
  ): Promise<void> {
    try {
      await axios.delete(
        `${this.config.host}/v2/console/message/${request.message_id}`,
        {
          headers: this.getAuthHeaders(),
          params: { channel_id: request.channel_id }
        }
      );
    } catch (error) {
      console.error('Failed to delete chat message:', error);
      throw error;
    }
  }

  public async updateChatMessage(
    serverKey: string,
    request: UpdateChatMessageRequest
  ): Promise<any> {
    try {
      const response = await axios.put(
        `${this.config.host}/v2/console/message/${request.message_id}`,
        { content: request.content },
        {
          headers: this.getAuthHeaders(),
          params: { channel_id: request.channel_id }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to update chat message:', error);
      throw error;
    }
  }

  public async sendChatMessage(
    serverKey: string,
    request: SendChatMessageRequest
  ): Promise<any> {
    try {
      const response = await axios.post(
        `${this.config.host}/v2/console/message`,
        request,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to send chat message:', error);
      throw error;
    }
  }

  public async listChatChannels(
    serverKey: string,
    filters?: ChatChannelSearchFilters,
    cursor?: string | null
  ): Promise<ChatChannelList> {
    try {
      const params = new URLSearchParams();
      
      if (filters?.room_name) {
        params.append('room_name', filters.room_name);
      }
      if (filters?.group_id) {
        params.append('group_id', filters.group_id);
      }
      if (filters?.user_id) {
        params.append('user_id', filters.user_id);
      }
      if (cursor) {
        params.append('cursor', cursor);
      }

      const response = await axios.get<ChatChannelList>(
        `${this.config.host}/v2/console/channel`,
        {
          headers: this.getAuthHeaders(),
          params: params
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to list chat channels:', error);
      throw error;
    }
  }

  public async getChatHistory(
    serverKey: string,
    channelId: string,
    cursor?: string,
    forward?: boolean,
    limit?: number
  ): Promise<ChatMessageList> {
    try {
      const params = new URLSearchParams();
      
      if (cursor) {
        params.append('cursor', cursor);
      }
      if (forward !== undefined) {
        params.append('forward', forward.toString());
      }
      if (limit) {
        params.append('limit', limit.toString());
      }

      const response = await axios.get<ChatMessageList>(
        `${this.config.host}/v2/console/channel/${channelId}/history`,
        {
          headers: this.getAuthHeaders(),
          params: params
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to get chat history:', error);
      throw error;
    }
  }

  // Notifications methods
  public async listNotifications(
    serverKey: string,
    filters?: NotificationSearchFilters,
    cursor?: string | null
  ): Promise<NotificationList> {
    try {
      const params = new URLSearchParams();
      
      if (filters?.user_id) {
        params.append('user_id', filters.user_id);
      }
      if (filters?.username) {
        params.append('username', filters.username);
      }
      if (filters?.subject) {
        params.append('subject', filters.subject);
      }
      if (filters?.content) {
        params.append('content', filters.content);
      }
      if (filters?.code !== undefined) {
        params.append('code', filters.code.toString());
      }
      if (filters?.sender_id) {
        params.append('sender_id', filters.sender_id);
      }
      if (filters?.persistent !== undefined) {
        params.append('persistent', filters.persistent.toString());
      }
      if (filters?.limit !== undefined) {
        params.append('limit', filters.limit.toString());
      } else {
        params.append('limit', '100'); // Default limit
      }
      if (cursor) {
        params.append('cursor', cursor);
      }

      const response = await axios.get<NotificationList>(
        `${this.config.host}/v2/console/notification`,
        {
          headers: this.getAuthHeaders(),
          params: params
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to list notifications:', error);
      throw error;
    }
  }

  public async getNotification(
    serverKey: string,
    notificationId: string,
    userId: string
  ): Promise<any> {
    try {
      const response = await axios.get(
        `${this.config.host}/v2/console/notification/${notificationId}`,
        {
          headers: this.getAuthHeaders(),
          params: { user_id: userId }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to get notification:', error);
      throw error;
    }
  }

  public async sendNotification(
    serverKey: string,
    request: SendNotificationRequest
  ): Promise<any> {
    try {
      const response = await axios.post(
        `${this.config.host}/v2/console/api/endpoints/rpc/send_custom_notification`,
        request,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to send notification:', error);
      throw error;
    }
  }

  public async sendNotificationBatch(
    serverKey: string,
    request: SendNotificationBatchRequest
  ): Promise<any> {
    try {
      const response = await axios.post(
        `${this.config.host}/v2/console/notification/batch`,
        request,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to send notification batch:', error);
      throw error;
    }
  }

  public async deleteNotification(
    serverKey: string,
    request: DeleteNotificationRequest
  ): Promise<void> {
    try {
      await axios.delete(
        `${this.config.host}/v2/console/notification/${request.notification_id}`,
        {
          headers: this.getAuthHeaders(),
          params: { user_id: request.user_id }
        }
      );
    } catch (error) {
      console.error('Failed to delete notification:', error);
      throw error;
    }
  }

  public async deleteNotificationBatch(
    serverKey: string,
    request: DeleteNotificationBatchRequest
  ): Promise<void> {
    try {
      await axios.delete(
        `${this.config.host}/v2/console/notification/batch`,
        {
          headers: this.getAuthHeaders(),
          data: request
        }
      );
    } catch (error) {
      console.error('Failed to delete notification batch:', error);
      throw error;
    }
  }

  public async listNotificationTemplates(
    serverKey: string,
    cursor?: string | null
  ): Promise<NotificationTemplateList> {
    try {
      const params = new URLSearchParams();
      
      if (cursor) {
        params.append('cursor', cursor);
      }

      const response = await axios.get<NotificationTemplateList>(
        `${this.config.host}/v2/console/notification/template`,
        {
          headers: this.getAuthHeaders(),
          params: params
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to list notification templates:', error);
      throw error;
    }
  }

  public async createNotificationTemplate(
    serverKey: string,
    request: CreateNotificationTemplateRequest
  ): Promise<any> {
    try {
      const response = await axios.post(
        `${this.config.host}/v2/console/notification/template`,
        request,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to create notification template:', error);
      throw error;
    }
  }

  public async updateNotificationTemplate(
    serverKey: string,
    request: UpdateNotificationTemplateRequest
  ): Promise<any> {
    try {
      const response = await axios.put(
        `${this.config.host}/v2/console/notification/template/${request.template_id}`,
        request,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to update notification template:', error);
      throw error;
    }
  }

  public async deleteNotificationTemplate(
    serverKey: string,
    request: DeleteNotificationTemplateRequest
  ): Promise<void> {
    try {
      await axios.delete(
        `${this.config.host}/v2/console/notification/template/${request.template_id}`,
        {
          headers: this.getAuthHeaders()
        }
      );
    } catch (error) {
      console.error('Failed to delete notification template:', error);
      throw error;
    }
  }

  public async getNotificationStats(
    serverKey: string
  ): Promise<NotificationStats> {
    try {
      const response = await axios.get<NotificationStats>(
        `${this.config.host}/v2/console/notification/stats`,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to get notification stats:', error);
      throw error;
    }
  }

  public async listUsers(
    filters?: UserSearchFilters,
    cursor?: string
  ): Promise<UserList> {
    try {
      const params = new URLSearchParams();
      
      if (filters?.filter) {
        params.append('filter', filters.filter);
      }
      
      if (filters?.tombstones) {
        params.append('tombstones', 'true');
      }
      
      if (cursor) {
        params.append('cursor', cursor);
      }

      const response = await axios.get<UserList>(
        `${this.config.host}/v2/console/user?${params.toString()}`,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to list users:', error);
      throw error;
    }
  }

  public async createUser(
    request: CreateUserRequest
  ): Promise<void> {
    try {
      await axios.post(
        `${this.config.host}/v2/console/user`,
        request,
        {
          headers: this.getAuthHeaders()
        }
      );
    } catch (error) {
      console.error('Failed to create user:', error);
      throw error;
    }
  }

  public async updateUser(
    request: UpdateUserRequest
  ): Promise<void> {
    try {
      await axios.put(
        `${this.config.host}/v2/console/user/${request.user_id}`,
        request,
        {
          headers: this.getAuthHeaders()
        }
      );
    } catch (error) {
      console.error('Failed to update user:', error);
      throw error;
    }
  }

  public async deleteUser(
    request: DeleteUserRequest
  ): Promise<void> {
    try {
      const params = new URLSearchParams();
      params.append('username', request.user_id);

      await axios.delete(
        `${this.config.host}/v2/console/user?${params.toString()}`,
        {
          headers: this.getAuthHeaders()
        }
      );
    } catch (error) {
      console.error('Failed to delete user:', error);
      throw error;
    }
  }

  public async banUser(
    request: BanUserRequest
  ): Promise<void> {
    try {
      await axios.post(
        `${this.config.host}/v2/console/account/${request.user_id}/ban`,
        {},
        {
          headers: this.getAuthHeaders()
        }
      );
    } catch (error) {
      console.error('Failed to ban user:', error);
      throw error;
    }
  }

  public async unbanUser(
    request: UnbanUserRequest
  ): Promise<void> {
    try {
      await axios.post(
        `${this.config.host}/v2/console/account/${request.user_id}/unban`,
        {},
        {
          headers: this.getAuthHeaders()
        }
      );
    } catch (error) {
      console.error('Failed to unban user:', error);
      throw error;
    }
  }

  // Configuration Methods
  public async getConfig(): Promise<Config> {
    try {
      const response = await axios.get<Config>(
        `${this.config.host}/v2/console/config`,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to get configuration:', error);
      throw error;
    }
  }

  public async updateConfig(
    request: UpdateConfigRequest
  ): Promise<void> {
    try {
      await axios.post(
        `${this.config.host}/v2/console/config`,
        request,
        {
          headers: this.getAuthHeaders()
        }
      );
    } catch (error) {
      console.error('Failed to update configuration:', error);
      throw error;
    }
  }

  // Runtime Modules Methods
  public async listRuntimeModules(): Promise<RuntimeModuleList> {
    try {
      const response = await axios.get<RuntimeModuleList>(
        `${this.config.host}/v2/console/runtime`,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to list runtime modules:', error);
      throw error;
    }
  }

  public async getRuntimeInfo(): Promise<RuntimeInfo> {
    try {
      const response = await axios.get<RuntimeInfo>(
        `${this.config.host}/v2/console/runtime/info`,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to get runtime info:', error);
      throw error;
    }
  }

  // Purchases Methods
  public async listPurchases(
    filters?: PurchaseSearchFilters,
    cursor?: string
  ): Promise<PurchaseList> {
    try {
      const params = new URLSearchParams();
      
      if (filters?.user_id) {
        params.append('user_id', filters.user_id);
      }
      
      if (filters?.product_id) {
        params.append('product_id', filters.product_id);
      }
      
      if (filters?.transaction_id) {
        params.append('transaction_id', filters.transaction_id);
      }
      
      if (filters?.store) {
        params.append('store', filters.store);
      }
      
      if (cursor) {
        params.append('cursor', cursor);
      }
      params.append('limit','100');
      const response = await axios.get<PurchaseList>(
        `${this.config.host}/v2/console/purchase?${params.toString()}`,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to list purchases:', error);
      throw error;
    }
  }

  public async validatePurchase(
    request: ValidatePurchaseRequest
  ): Promise<void> {
    try {
      await axios.post(
        `${this.config.host}/v2/console/purchase/validate`,
        request,
        {
          headers: this.getAuthHeaders()
        }
      );
    } catch (error) {
      console.error('Failed to validate purchase:', error);
      throw error;
    }
  }

  // Subscriptions Methods
  public async listSubscriptions(
    filters?: SubscriptionSearchFilters,
    cursor?: string
  ): Promise<SubscriptionList> {
    try {
      const params = new URLSearchParams();
      
      if (filters?.user_id) {
        params.append('user_id', filters.user_id);
      }
      
      if (filters?.product_id) {
        params.append('product_id', filters.product_id);
      }
      
      if (filters?.store) {
        params.append('store', filters.store);
      }
      
      if (filters?.active !== undefined) {
        params.append('active', filters.active.toString());
      }
      
      if (cursor) {
        params.append('cursor', cursor);
      }
      params.append('limit','100')
      const response = await axios.get<SubscriptionList>(
        `${this.config.host}/v2/console/subscription?${params.toString()}`,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to list subscriptions:', error);
      throw error;
    }
  }

  // Matches Methods
  public async listMatches(
    filters?: MatchSearchFilters
  ): Promise<MatchList> {
    try {
      const params = new URLSearchParams();
      
      if (filters?.node) {
        params.append('node', filters.node);
      }
      
      if (filters?.authoritative !== undefined) {
        params.append('authoritative', filters.authoritative.toString());
      }
      
      if (filters?.label) {
        params.append('label', filters.label);
      }
      
      if (filters?.min_size !== undefined) {
        params.append('min_size', filters.min_size.toString());
      }
      
      if (filters?.max_size !== undefined) {
        params.append('max_size', filters.max_size.toString());
      }

      const response = await axios.get<MatchList>(
        `${this.config.host}/v2/console/match?${params.toString()}`,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to list matches:', error);
      throw error;
    }
  }

  // Analytics Methods
  public async getAnalyticsDashboard(filters?: AnalyticsFilters): Promise<AnalyticsResponse> {
    try {
      const params = new URLSearchParams();
      
      if (filters?.dateRange?.startDate) {
        params.append('start_date', filters.dateRange.startDate);
      }
      
      if (filters?.dateRange?.endDate) {
        params.append('end_date', filters.dateRange.endDate);
      }
      
      if (filters?.gameIds && filters.gameIds.length > 0) {
        params.append('game_ids', filters.gameIds.join(','));
      }
      
      if (filters?.userSegment && filters.userSegment !== 'all') {
        params.append('user_segment', filters.userSegment);
      }
      
      if (filters?.platform && filters.platform !== 'all') {
        params.append('platform', filters.platform);
      }
      

      const response = await axios.get<AnalyticsResponse>(
        `${this.config.host}/v2/console/analytics/dashboard?${params.toString()}`,
        {
          headers: this.getAuthHeaders()
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch analytics data');
      }

      return response.data;
    } catch (error) {
      console.error('Failed to get analytics dashboard:', error);
      
      // Return mock data for development/demo purposes
      return {
        success: true,
        data: this.getMockAnalyticsData(),
        message: 'Mock data loaded'
      };
    }
  }

  public async exportAnalytics(
    options: ExportOptions,
    filters: AnalyticsFilters
  ): Promise<ExportResponse> {
    try {
      const payload = {
        options,
        filters
      };

      const response = await axios.post<ExportResponse>(
        `${this.config.host}/v2/console/analytics/export`,
        payload,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to export analytics:', error);
      throw error;
    }
  }

  // Mock data for development/demo purposes
  private getMockAnalyticsData(): DashboardOverview {
    return {
      userMetrics: {
        totalUsers: 15420,
        activeUsers: 8750,
        inactiveUsers: 5890,
        bannedUsers: 780,
        dailyActiveUsers: 3200,
        weeklyActiveUsers: 6800,
        monthlyActiveUsers: 8750,
        newUsersToday: 145,
        newUsersThisWeek: 890,
        newUsersThisMonth: 2340
      },
      gameMetrics: [
        {
          gameId: 'ludo',
          gameName: 'Ludo',
          totalPlays: 45680,
          uniquePlayers: 5420,
          averageSessionLength: 12.5,
          totalSessionTime: 571000,
          singlePlayerGames: 18270,
          multiPlayerGames: 27410,
          completionRate: 78.5,
          averageScore: 1250,
          topScore: 4890
        },
        {
          gameId: 'quiz',
          gameName: 'Quiz',
          totalPlays: 32150,
          uniquePlayers: 4180,
          averageSessionLength: 8.2,
          totalSessionTime: 263630,
          singlePlayerGames: 28340,
          multiPlayerGames: 3810,
          completionRate: 85.2,
          averageScore: 850,
          topScore: 1500
        },
        {
          gameId: 'solitaire',
          gameName: 'Solitaire',
          totalPlays: 28940,
          uniquePlayers: 3650,
          averageSessionLength: 15.8,
          totalSessionTime: 457252,
          singlePlayerGames: 28940,
          multiPlayerGames: 0,
          completionRate: 42.3,
          averageScore: 2150,
          topScore: 8750
        }
      ],
      coinMetrics: {
        totalCoinsInCirculation: 2450000,
        totalCoinsEarned: 3890000,
        totalCoinsSpent: 1440000,
        averageCoinsPerUser: 159,
        topCoinEarner: {
          userId: 'user_123',
          username: 'GameMaster2024',
          coins: 45890
        },
        coinTransactions: {
          earned: 3890000,
          spent: 1440000,
          purchased: 890000,
          gifted: 125000
        }
      },
      adMetrics: {
        totalAdViews: 125680,
        rewardedAdViews: 89420,
        interstitialAdViews: 36260,
        bannerAdImpressions: 456780,
        adRevenue: 3450.75,
        averageAdViewsPerUser: 8.2,
        adClickThroughRate: 2.8,
        topAdNetwork: 'AdMob'
      },
      leaderboardMetrics: {
        globalLeaderboard: {
          topPlayer: {
            userId: 'user_456',
            username: 'CoinCollector',
            totalCoins: 45890
          },
          averageRank: 7712,
          totalRankedPlayers: 15424
        },
        gameLeaderboards: {
          ludo: {
            topPlayer: {
              userId: 'user_789',
              username: 'LudoKing',
              score: 4890
            },
            averageScore: 1250,
            totalPlayers: 5420
          },
          quiz: {
            topPlayer: {
              userId: 'user_101',
              username: 'BrainMaster',
              score: 1500
            },
            averageScore: 850,
            totalPlayers: 4180
          },
          solitaire: {
            topPlayer: {
              userId: 'user_202',
              username: 'CardShark',
              score: 8750
            },
            averageScore: 2150,
            totalPlayers: 3650
          }
        }
      },
      quizMetrics: {
        totalQuestions: 2450,
        totalCategories: 12,
        averageCorrectAnswers: 3.2,
        mostPopularCategory: 'General Knowledge',
        averageLifelinesUsed: 1.8,
        totalCoinsSpentOnLifelines: 145000,
        fastestCompletionTime: 45,
        averageCompletionTime: 180
      },
      gameModeMetrics: [
        {
          gameId: 'ludo',
          mode: 'online_multiplayer',
          totalPlays: 18500,
          uniquePlayers: 3200,
          averageSessionLength: 14.2,
          winRate: 25.0
        },
        {
          gameId: 'ludo',
          mode: 'vs_ai',
          totalPlays: 12800,
          uniquePlayers: 2100,
          averageSessionLength: 10.5,
          winRate: 68.5
        },
        {
          gameId: 'ludo',
          mode: 'pass_n_play',
          totalPlays: 8900,
          uniquePlayers: 1800,
          averageSessionLength: 16.8,
          winRate: 50.0
        },
        {
          gameId: 'ludo',
          mode: 'friends',
          totalPlays: 5480,
          uniquePlayers: 1420,
          averageSessionLength: 13.2,
          winRate: 45.2
        }
      ],
      recentActivity: [
        {
          id: '1',
          timestamp: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
          userId: 'user_123',
          username: 'GameMaster2024',
          action: 'completed game',
          gameId: 'ludo',
          gameName: 'Ludo',
          details: 'Won multiplayer match',
          coinsInvolved: 150
        },
        {
          id: '2',
          timestamp: new Date(Date.now() - 12 * 60 * 1000).toISOString(),
          userId: 'user_456',
          username: 'QuizWhiz',
          action: 'used lifeline',
          gameId: 'quiz',
          gameName: 'Quiz',
          details: 'Used 50/50 lifeline',
          coinsInvolved: -5
        },
        {
          id: '3',
          timestamp: new Date(Date.now() - 18 * 60 * 1000).toISOString(),
          userId: 'user_789',
          username: 'CardMaster',
          action: 'completed game',
          gameId: 'solitaire',
          gameName: 'Solitaire',
          details: 'Finished in 3:45',
          coinsInvolved: 200
        },
        {
          id: '4',
          timestamp: new Date(Date.now() - 25 * 60 * 1000).toISOString(),
          userId: 'user_101',
          username: 'NewPlayer',
          action: 'registered',
          details: 'New user signup',
          coinsInvolved: 100
        },
        {
          id: '5',
          timestamp: new Date(Date.now() - 32 * 60 * 1000).toISOString(),
          userId: 'user_202',
          username: 'LudoFan',
          action: 'watched ad',
          details: 'Rewarded video ad',
          coinsInvolved: 50
        }
      ]
    };
  }

  // Ad Management Methods
  public async getAdManagementDashboard(): Promise<AdManagementDashboard> {
    try {
      const response = await axios.get<AdManagementDashboard>(
        `${this.config.host}/v2/console/ad-management/dashboard`,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to get ad management dashboard:', error);
      
      // Return mock data for development/demo purposes
      return this.getMockAdManagementData();
    }
  }

  public async createAdNetwork(request: CreateAdNetworkRequest): Promise<AdNetworkConfig> {
    try {
      const response = await axios.post<AdNetworkConfig>(
        `${this.config.host}/v2/console/ad-management/networks`,
        request,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to create ad network:', error);
      throw error;
    }
  }

  public async updateAdNetwork(request: UpdateAdNetworkRequest): Promise<AdNetworkConfig> {
    try {
      const response = await axios.put<AdNetworkConfig>(
        `${this.config.host}/v2/console/ad-management/networks/${request.id}`,
        request,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to update ad network:', error);
      throw error;
    }
  }

  public async testAdNetwork(networkId: string, platform: Platform, adType: AdType): Promise<AdNetworkTestResult> {
    try {
      const response = await axios.post<AdNetworkTestResult>(
        `${this.config.host}/v2/console/ad-management/networks/${networkId}/test`,
        { platform, adType },
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to test ad network:', error);
      throw error;
    }
  }

  // Mock data for development/demo purposes
  private getMockAdManagementData(): AdManagementDashboard {
    return {
      networks: [
        {
          id: 'admob_1',
          network: 'admob' as any,
          name: 'AdMob Primary',
          enabled: true,
          priority: 1,
          platformConfigs: {
            android: {
              appId: 'ca-app-pub-1234567890123456~1234567890',
              adUnits: {
                interstitial: {
                  unitId: 'ca-app-pub-1234567890123456/1234567890',
                  enabled: true,
                  testUnitId: 'ca-app-pub-3940256099942544/1033173712'
                },
                rewarded: {
                  unitId: 'ca-app-pub-1234567890123456/1234567891',
                  enabled: true,
                  testUnitId: 'ca-app-pub-3940256099942544/5224354917'
                },
                banner: {
                  unitId: 'ca-app-pub-1234567890123456/1234567892',
                  enabled: true,
                  testUnitId: 'ca-app-pub-3940256099942544/6300978111'
                }
              }
            },
            ios: {
              appId: 'ca-app-pub-1234567890123456~1234567891',
              adUnits: {
                interstitial: {
                  unitId: 'ca-app-pub-1234567890123456/1234567893',
                  enabled: true,
                  testUnitId: 'ca-app-pub-3940256099942544/4411468910'
                },
                rewarded: {
                  unitId: 'ca-app-pub-1234567890123456/1234567894',
                  enabled: true,
                  testUnitId: 'ca-app-pub-3940256099942544/1712485313'
                },
                banner: {
                  unitId: 'ca-app-pub-1234567890123456/1234567895',
                  enabled: true,
                  testUnitId: 'ca-app-pub-3940256099942544/2934735716'
                }
              }
            }
          },
          testMode: false,
          createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          updatedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString()
        },
        {
          id: 'unity_1',
          network: 'unity_ads' as any,
          name: 'Unity Ads Secondary',
          enabled: true,
          priority: 2,
          platformConfigs: {
            android: {
              appId: '1234567',
              adUnits: {
                interstitial: {
                  unitId: 'Interstitial_Android',
                  enabled: true
                },
                rewarded: {
                  unitId: 'Rewarded_Android',
                  enabled: true
                }
              }
            },
            ios: {
              appId: '1234568',
              adUnits: {
                interstitial: {
                  unitId: 'Interstitial_iOS',
                  enabled: true
                },
                rewarded: {
                  unitId: 'Rewarded_iOS',
                  enabled: true
                }
              }
            }
          },
          testMode: true,
          createdAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
          updatedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString()
        }
      ],
      frequencyConfigs: [
        {
          id: 'freq_1',
          adType: 'interstitial' as any,
          enabled: true,
          rules: [
            {
              id: 'rule_1',
              name: 'Game End Interstitial',
              description: 'Show interstitial ad after every 3 games',
              trigger: 'game_end' as any,
              cooldown: 30,
              maxPerSession: 5,
              maxPerDay: 20,
              enabled: true
            }
          ],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        },
        {
          id: 'freq_2',
          adType: 'rewarded' as any,
          enabled: true,
          rules: [
            {
              id: 'rule_2',
              name: 'Coin Reward',
              description: 'Rewarded ad for extra coins',
              trigger: 'manual_request' as any,
              cooldown: 300,
              maxPerSession: 10,
              maxPerDay: 50,
              enabled: true
            }
          ],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      ],
      rewardedAds: [
        {
          id: 'reward_1',
          name: 'Extra Coins',
          description: 'Watch an ad to earn 50 bonus coins',
          rewardType: 'coins',
          rewardAmount: 50,
          enabled: true,
          availableGames: ['ludo', 'quiz', 'solitaire'],
          cooldownMinutes: 5,
          maxRewardsPerDay: 10,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        },
        {
          id: 'reward_2',
          name: 'Quiz Lifelines',
          description: 'Get all lifelines back for quiz',
          rewardType: 'lifelines',
          rewardAmount: 4,
          enabled: true,
          availableGames: ['quiz'],
          cooldownMinutes: 10,
          maxRewardsPerDay: 5,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      ],
      placements: [
        {
          id: 'placement_1',
          name: 'Ludo Game End',
          gameId: 'ludo',
          gameName: 'Ludo',
          adType: 'interstitial' as any,
          placement: 'game_end' as any,
          enabled: true,
          frequency: 3,
          priority: 1,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        },
        {
          id: 'placement_2',
          name: 'Quiz Reward Button',
          gameId: 'quiz',
          gameName: 'Quiz',
          adType: 'rewarded' as any,
          placement: 'main_menu' as any,
          enabled: true,
          frequency: 1,
          priority: 1,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      ],
      revenueReport: {
        totalRevenue: 1250.75,
        totalImpressions: 45680,
        totalClicks: 1280,
        averageCtr: 2.8,
        averageEcpm: 2.74,
        topPerformingNetwork: 'admob' as any,
        performanceByNetwork: [
          {
            networkId: 'admob_1',
            network: 'admob' as any,
            platform: 'android' as any,
            adType: 'interstitial' as any,
            impressions: 25000,
            clicks: 700,
            revenue: 685.50,
            fillRate: 95.2,
            ctr: 2.8,
            ecpm: 2.74,
            date: new Date().toISOString().split('T')[0]
          },
          {
            networkId: 'unity_1',
            network: 'unity_ads' as any,
            platform: 'android' as any,
            adType: 'rewarded' as any,
            impressions: 20680,
            clicks: 580,
            revenue: 565.25,
            fillRate: 88.5,
            ctr: 2.8,
            ecpm: 2.73,
            date: new Date().toISOString().split('T')[0]
          }
        ],
        performanceByType: [],
        performanceByPlatform: [],
        dateRange: {
          startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
          endDate: new Date().toISOString()
        }
      },
      summary: {
        totalNetworks: 2,
        activeNetworks: 2,
        totalPlacements: 2,
        activePlacements: 2,
        totalRewardedAds: 2,
        activeRewardedAds: 2,
        dailyRevenue: 178.68,
        dailyImpressions: 6525
      }
    };
  }

  // Coin Management Methods
  public async getCoinManagementDashboard(): Promise<CoinManagementDashboard> {
    try {
      const response = await axios.get<CoinManagementDashboard>(
        `${this.config.host}/v2/console/coin-management/dashboard`,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to get coin management dashboard:', error);
      
      // Return mock data for development/demo purposes
      return this.getMockCoinManagementData();
    }
  }

  public async createCoinPackage(request: CreateCoinPackageRequest): Promise<any> {
    try {
      const response = await axios.post(
        `${this.config.host}/v2/console/coin-management/packages`,
        request,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to create coin package:', error);
      throw error;
    }
  }

  public async adjustUserCoins(request: ManualAdjustment): Promise<any> {
    try {
      const response = await axios.post(
        `${this.config.host}/v2/console/coin-management/adjust`,
        request,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to adjust user coins:', error);
      throw error;
    }
  }

  // Mock data for development/demo purposes
  private getMockCoinManagementData(): CoinManagementDashboard {
    return {
      packages: [
        {
          id: 'pkg_1',
          name: 'Starter Pack',
          description: 'Perfect for new players to get started',
          coinAmount: 100,
          price: 0.99,
          currency: 'USD',
          bonusCoins: 20,
          enabled: true,
          featured: false,
          icon: '🪙',
          productId: 'com.investaplay.coins.starter',
          platformPricing: {
            android: {
              productId: 'com.investaplay.coins.starter',
              price: 0.99,
              currency: 'USD',
              localizedPrice: '$0.99'
            },
            ios: {
              productId: 'com.investaplay.coins.starter',
              price: 0.99,
              currency: 'USD',
              localizedPrice: '$0.99'
            }
          },
          purchaseCount: 1250,
          revenue: 1237.50,
          createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          updatedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString()
        },
        {
          id: 'pkg_2',
          name: 'Popular Pack',
          description: 'Most popular choice among players',
          coinAmount: 500,
          price: 4.99,
          currency: 'USD',
          bonusCoins: 150,
          enabled: true,
          featured: true,
          icon: '💰',
          productId: 'com.investaplay.coins.popular',
          platformPricing: {
            android: {
              productId: 'com.investaplay.coins.popular',
              price: 4.99,
              currency: 'USD',
              localizedPrice: '$4.99'
            },
            ios: {
              productId: 'com.investaplay.coins.popular',
              price: 4.99,
              currency: 'USD',
              localizedPrice: '$4.99'
            }
          },
          purchaseCount: 2850,
          revenue: 14221.50,
          createdAt: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000).toISOString(),
          updatedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString()
        },
        {
          id: 'pkg_3',
          name: 'Mega Pack',
          description: 'Best value for serious players',
          coinAmount: 1000,
          price: 9.99,
          currency: 'USD',
          bonusCoins: 400,
          enabled: true,
          featured: true,
          discountPercentage: 20,
          originalPrice: 12.49,
          icon: '💎',
          productId: 'com.investaplay.coins.mega',
          platformPricing: {
            android: {
              productId: 'com.investaplay.coins.mega',
              price: 9.99,
              currency: 'USD',
              localizedPrice: '$9.99'
            },
            ios: {
              productId: 'com.investaplay.coins.mega',
              price: 9.99,
              currency: 'USD',
              localizedPrice: '$9.99'
            }
          },
          purchaseCount: 1680,
          revenue: 16783.20,
          createdAt: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
          updatedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString()
        }
      ],
      bonusConfigs: [
        {
          id: 'bonus_1',
          type: BonusType.SIGNUP,
          name: 'Welcome Bonus',
          description: 'New player welcome bonus',
          coinAmount: 50,
          enabled: true,
          maxClaims: 1,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        },
        {
          id: 'bonus_2',
          type: BonusType.DAILY_LOGIN,
          name: 'Daily Login Reward',
          description: 'Daily login bonus for active players',
          coinAmount: 10,
          enabled: true,
          cooldownHours: 24,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      ],
      activeEvents: [],
      recentTransactions: [
        {
          id: 'tx_1',
          userId: 'user_123',
          username: 'GameMaster',
          type: TransactionType.PURCHASE,
          source: TransactionSource.STORE_PURCHASE,
          amount: 650,
          balanceBefore: 150,
          balanceAfter: 800,
          description: 'Popular Pack purchase',
          timestamp: new Date(Date.now() - 10 * 60 * 1000).toISOString()
        },
        {
          id: 'tx_2',
          userId: 'user_456',
          username: 'QuizMaster',
          type: TransactionType.EARNED,
          source: TransactionSource.GAME_QUIZ,
          amount: 25,
          balanceBefore: 75,
          balanceAfter: 100,
          description: 'Quiz game completion reward',
          timestamp: new Date(Date.now() - 15 * 60 * 1000).toISOString()
        },
        {
          id: 'tx_3',
          userId: 'user_789',
          username: 'LudoChamp',
          type: TransactionType.SPENT,
          source: TransactionSource.LIFELINE_PURCHASE,
          amount: -30,
          balanceBefore: 200,
          balanceAfter: 170,
          description: 'Quiz lifeline purchase',
          timestamp: new Date(Date.now() - 20 * 60 * 1000).toISOString()
        },
        {
          id: 'tx_4',
          userId: 'user_101',
          username: 'NewPlayer',
          type: TransactionType.BONUS,
          source: TransactionSource.BONUS_SIGNUP,
          amount: 50,
          balanceBefore: 0,
          balanceAfter: 50,
          description: 'Welcome bonus for new player',
          timestamp: new Date(Date.now() - 25 * 60 * 1000).toISOString()
        },
        {
          id: 'tx_5',
          userId: 'user_202',
          username: 'AdWatcher',
          type: TransactionType.EARNED,
          source: TransactionSource.AD_REWARD,
          amount: 50,
          balanceBefore: 120,
          balanceAfter: 170,
          description: 'Rewarded ad completion',
          timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString()
        }
      ],
      analytics: {
        totalCoinsInCirculation: 2450000,
        totalCoinsEarned: 1850000,
        totalCoinsSpent: 1200000,
        totalCoinsPurchased: 800000,
        averageCoinsPerUser: 245,
        coinVelocity: 0.65,
        topSpenders: [],
        topEarners: [],
        transactionsBySource: {} as any,
        packagePerformance: [],
        bonusDistribution: [],
        dailyStats: []
      },
      summary: {
        totalPackages: 3,
        activePackages: 3,
        totalBonuses: 2,
        activeBonuses: 2,
        activeEvents: 0,
        dailyRevenue: 485.75,
        dailyTransactions: 156,
        totalCoinsInCirculation: 2450000
      }
    };
  }

  // Quiz Management Methods
  public async getQuizManagementDashboard(): Promise<QuizManagementDashboard> {
    try {
      const response = await axios.get<QuizManagementDashboard>(
        `${this.config.host}/v2/console/quiz-management/dashboard`,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to get quiz management dashboard:', error);
      
      // Return mock data for development/demo purposes
      return this.getMockQuizManagementData();
    }
  }

  // Quiz category endpoints (real + mock fallback)
  public async getQuizCategories(): Promise<QuestionCategoryInfo[]> {
    try {
      const rpcResponse = await this.callRpcFunction({
        id: 'quiz_categories_get',
        body: JSON.stringify({}),
        user_id: '00000000-0000-0000-0000-000000000000',
        http_key: 'game_asDFGHyhu_http487ewmdc04356lvdme09'
      });

      if (rpcResponse.body) {
        const responseData: BackendQuizCategoriesResponse = JSON.parse(rpcResponse.body);
        return this.mapBackendCategoriesToFrontend(responseData);
      }

      throw new Error('No response body from quiz_categories_get');
    } catch (error) {
      console.error('Failed to get quiz categories from RPC endpoint:', error);
      // Fallback to mock data
      return this.getQuizCategoriesMock();
    }
  }

  private mapBackendCategoriesToFrontend(response: BackendQuizCategoriesResponse): QuestionCategoryInfo[] {
    const categoryMap: Record<string, QuestionCategory> = {
      'Science': QuestionCategory.SCIENCE,
      'Sport': QuestionCategory.SPORTS,
      'Technology': QuestionCategory.TECHNOLOGY,
      'History': QuestionCategory.HISTORY,
      'Geography': QuestionCategory.GEOGRAPHY,
      'Entertainment': QuestionCategory.ENTERTAINMENT,
      'Literature': QuestionCategory.LITERATURE,
      'Mathematics': QuestionCategory.MATHEMATICS,
      'Politics': QuestionCategory.POLITICS,
      'General Knowledge': QuestionCategory.GENERAL_KNOWLEDGE,
      'General': QuestionCategory.GENERAL_KNOWLEDGE
    };

    const iconMap: Record<string, string> = {
      [QuestionCategory.SCIENCE]: '🔬',
      [QuestionCategory.SPORTS]: '⚽',
      [QuestionCategory.TECHNOLOGY]: '💻',
      [QuestionCategory.HISTORY]: '🏛️',
      [QuestionCategory.GEOGRAPHY]: '🌍',
      [QuestionCategory.ENTERTAINMENT]: '🎬',
      [QuestionCategory.LITERATURE]: '📚',
      [QuestionCategory.MATHEMATICS]: '🔢',
      [QuestionCategory.POLITICS]: '🏛️',
      [QuestionCategory.GENERAL_KNOWLEDGE]: '🧠'
    };

    const colorMap: Record<string, string> = {
      [QuestionCategory.SCIENCE]: '#20c997',
      [QuestionCategory.SPORTS]: '#28a745',
      [QuestionCategory.TECHNOLOGY]: '#6f42c1',
      [QuestionCategory.HISTORY]: '#fd7e14',
      [QuestionCategory.GEOGRAPHY]: '#17a2b8',
      [QuestionCategory.ENTERTAINMENT]: '#e83e8c',
      [QuestionCategory.LITERATURE]: '#6c757d',
      [QuestionCategory.MATHEMATICS]: '#ffc107',
      [QuestionCategory.POLITICS]: '#dc3545',
      [QuestionCategory.GENERAL_KNOWLEDGE]: '#007bff'
    };

    const categories: QuestionCategoryInfo[] = [];
    const categoryGroups: Record<string, BackendQuizCategory[]> = {};

    // Group categories by categoryName
    Object.values(response.categories).forEach((cat) => {
      if (!categoryGroups[cat.categoryName]) {
        categoryGroups[cat.categoryName] = [];
      }
      categoryGroups[cat.categoryName].push(cat);
    });

    // Create one QuestionCategoryInfo per unique category name
    Object.entries(categoryGroups).forEach(([categoryName, backendCats]) => {
      const frontendCategory = categoryMap[categoryName] || QuestionCategory.GENERAL_KNOWLEDGE;
      const activeCats = backendCats.filter(c => c.isActive);
      const totalCount = backendCats.length;
      const activeCount = activeCats.length;
      
      // Calculate average difficulty (convert backend 0/1/2 to 1/2/3 scale)
      const avgDifficulty = activeCats.length > 0
        ? activeCats.reduce((sum, c) => sum + (c.difficulty + 1), 0) / activeCats.length
        : 1;

      categories.push({
        category: frontendCategory,
        name: categoryName,
        description: `${categoryName} quiz questions`,
        icon: iconMap[frontendCategory] || '❓',
        color: colorMap[frontendCategory] || '#6c757d',
        totalQuestions: totalCount,
        activeQuestions: activeCount,
        averageDifficulty: avgDifficulty,
        popularityScore: activeCount > 0 ? (activeCount / totalCount) * 10 : 0,
        lastUpdated: new Date().toISOString()
      });
    });

    return categories;
  }

  // Mock quiz category endpoints (in-memory only, used as fallback)
  private ensureQuizCategoriesMockInitialized(): void {
    if (!this.quizCategoriesMock) {
      const mock = this.getMockQuizManagementData();
      this.quizCategoriesMock = mock.categories.map(c => ({ ...c }));
    }
  }

  public async getQuizCategoriesMock(): Promise<QuestionCategoryInfo[]> {
    this.ensureQuizCategoriesMockInitialized();
    return Promise.resolve(this.quizCategoriesMock as QuestionCategoryInfo[]);
  }

  public async addQuizCategory(request: AddQuizCategoryRequest): Promise<any> {
    try {
      const rpcResponse = await this.callRpcFunction({
        id: 'quiz_category_add',
        body: JSON.stringify(request),
        user_id: '00000000-0000-0000-0000-000000000000',
        http_key: 'game_asDFGHyhu_http487ewmdc04356lvdme09'
      });

      if (rpcResponse.body) {
        return JSON.parse(rpcResponse.body);
      }

      return rpcResponse;
    } catch (error) {
      console.error('Failed to add quiz category via RPC endpoint:', error);
      throw error;
    }
  }

  public async removeQuizCategory(request: RemoveQuizCategoryRequest): Promise<any> {
    try {
      const rpcResponse = await this.callRpcFunction({
        id: 'quiz_category_remove',
        body: JSON.stringify(request),
        user_id: '00000000-0000-0000-0000-000000000000',
        http_key: 'game_asDFGHyhu_http487ewmdc04356lvdme09'
      });

      if (rpcResponse.body) {
        return JSON.parse(rpcResponse.body);
      }

      return rpcResponse;
    } catch (error) {
      console.error('Failed to remove quiz category via RPC endpoint:', error);
      throw error;
    }
  }

  public async addQuizQuestionToCategory(request: AddQuizQuestionToCategoryRequest): Promise<any> {
    try {
      const rpcResponse = await this.callRpcFunction({
        id: 'quiz_question_add_to_category',
        body: JSON.stringify({
          ...request,
          correctOptionIndex: this.toBackendQuizCorrectOptionIndex(request.correctOptionIndex)
        }),
        user_id: '00000000-0000-0000-0000-000000000000',
        http_key: 'game_asDFGHyhu_http487ewmdc04356lvdme09'
      });

      if (rpcResponse.body) {
        return JSON.parse(rpcResponse.body);
      }

      return rpcResponse;
    } catch (error) {
      console.error('Failed to add quiz question via RPC endpoint:', error);
      throw error;
    }
  }

  public async removeQuizQuestionFromCategory(request: RemoveQuizQuestionFromCategoryRequest): Promise<any> {
    try {
      const rpcResponse = await this.callRpcFunction({
        id: 'quiz_question_remove_from_category',
        body: JSON.stringify(request),
        user_id: '00000000-0000-0000-0000-000000000000',
        http_key: 'game_asDFGHyhu_http487ewmdc04356lvdme09'
      });

      if (rpcResponse.body) {
        return JSON.parse(rpcResponse.body);
      }

      return rpcResponse;
    } catch (error) {
      console.error('Failed to remove quiz question via RPC endpoint:', error);
      throw error;
    }
  }

  public async getQuizQuestionsByCategory(request: GetQuizQuestionsByCategoryRequest): Promise<QuizQuestionByCategoryResponse> {
    try {
      const rpcResponse = await this.callRpcFunction({
        id: 'quiz_question_get_by_category',
        body: JSON.stringify(request),
        user_id: '00000000-0000-0000-0000-000000000000',
        http_key: 'game_asDFGHyhu_http487ewmdc04356lvdme09'
      });

      if (rpcResponse.body) {
        return JSON.parse(rpcResponse.body);
      }

      return rpcResponse as QuizQuestionByCategoryResponse;
    } catch (error) {
      console.error('Failed to get quiz questions by category via RPC endpoint:', error);
      throw error;
    }
  }

  public async createQuizCategoryMock(request: CreateQuestionCategoryRequest): Promise<QuestionCategoryInfo> {
    this.ensureQuizCategoriesMockInitialized();

    const now = new Date().toISOString();
    const newCategory: QuestionCategoryInfo = {
      category: request.category,
      name: request.name,
      description: request.description,
      icon: request.icon,
      color: request.color,
      totalQuestions: 0,
      activeQuestions: 0,
      averageDifficulty: 1,
      popularityScore: 0,
      lastUpdated: now
    };

    // Replace if category already exists
    const existingIndex = (this.quizCategoriesMock as QuestionCategoryInfo[]).findIndex(
      c => c.category === request.category
    );
    if (existingIndex >= 0) {
      (this.quizCategoriesMock as QuestionCategoryInfo[])[existingIndex] = newCategory;
    } else {
      (this.quizCategoriesMock as QuestionCategoryInfo[]).push(newCategory);
    }

    return Promise.resolve(newCategory);
  }

  public async updateQuizCategoryMock(request: UpdateQuestionCategoryRequest): Promise<QuestionCategoryInfo | null> {
    this.ensureQuizCategoriesMockInitialized();
    const categories = this.quizCategoriesMock as QuestionCategoryInfo[];
    const index = categories.findIndex(c => c.category === request.category);
    if (index === -1) {
      return Promise.resolve(null);
    }

    const current = categories[index];
    const updated: QuestionCategoryInfo = {
      ...current,
      name: request.name ?? current.name,
      description: request.description ?? current.description,
      icon: request.icon ?? current.icon,
      color: request.color ?? current.color,
      lastUpdated: new Date().toISOString()
    };

    categories[index] = updated;
    return Promise.resolve(updated);
  }

  public async deleteQuizCategoryMock(request: DeleteQuestionCategoryRequest): Promise<void> {
    this.ensureQuizCategoriesMockInitialized();
    this.quizCategoriesMock = (this.quizCategoriesMock as QuestionCategoryInfo[]).filter(
      c => c.category !== request.category
    );
    return Promise.resolve();
  }

  public async createQuestion(request: CreateQuestionRequest): Promise<any> {
    try {
      const response = await axios.post(
        `${this.config.host}/v2/console/quiz-management/questions`,
        request,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to create question:', error);
      throw error;
    }
  }

  public async updateQuestion(request: UpdateQuestionRequest): Promise<any> {
    try {
      const response = await axios.put(
        `${this.config.host}/v2/console/quiz-management/questions/${request.id}`,
        request,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to update question:', error);
      throw error;
    }
  }

  public async deleteQuestion(request: DeleteQuestionRequest): Promise<any> {
    try {
      const response = await axios.delete(
        `${this.config.host}/v2/console/quiz-management/questions/${request.id}`,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to delete question:', error);
      throw error;
    }
  }

  // Mock data for development/demo purposes
  private getMockQuizManagementData(): QuizManagementDashboard {
    return {
      questions: [
        {
          id: 'q1',
          question: 'What is the capital of France?',
          type: QuestionType.MULTIPLE_CHOICE,
          category: QuestionCategory.GEOGRAPHY,
          difficulty: QuestionDifficulty.EASY,
          status: QuestionStatus.ACTIVE,
          options: [
            { id: 'opt1', text: 'Paris', isCorrect: true },
            { id: 'opt2', text: 'London', isCorrect: false },
            { id: 'opt3', text: 'Berlin', isCorrect: false },
            { id: 'opt4', text: 'Madrid', isCorrect: false }
          ],
          correctAnswer: 'Paris',
          explanation: 'Paris is the capital and largest city of France.',
          points: 10,
          timeLimit: 30,
          tags: ['geography', 'capitals', 'europe'],
          language: 'en',
          createdBy: 'admin',
          createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
          updatedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          usageCount: 1250,
          correctAnswerRate: 85.2,
          averageResponseTime: 12.5
        },
        {
          id: 'q2',
          question: 'Which programming language is known for its use in web development?',
          type: QuestionType.MULTIPLE_CHOICE,
          category: QuestionCategory.TECHNOLOGY,
          difficulty: QuestionDifficulty.MEDIUM,
          status: QuestionStatus.ACTIVE,
          options: [
            { id: 'opt1', text: 'JavaScript', isCorrect: true },
            { id: 'opt2', text: 'Assembly', isCorrect: false },
            { id: 'opt3', text: 'COBOL', isCorrect: false },
            { id: 'opt4', text: 'Fortran', isCorrect: false }
          ],
          correctAnswer: 'JavaScript',
          explanation: 'JavaScript is widely used for web development, both frontend and backend.',
          points: 15,
          timeLimit: 45,
          tags: ['programming', 'web', 'javascript'],
          language: 'en',
          createdBy: 'admin',
          createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
          updatedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
          usageCount: 890,
          correctAnswerRate: 72.8,
          averageResponseTime: 18.3
        },
        {
          id: 'q3',
          question: 'Who won the FIFA World Cup in 2018?',
          type: QuestionType.MULTIPLE_CHOICE,
          category: QuestionCategory.SPORTS,
          difficulty: QuestionDifficulty.EASY,
          status: QuestionStatus.ACTIVE,
          options: [
            { id: 'opt1', text: 'France', isCorrect: true },
            { id: 'opt2', text: 'Brazil', isCorrect: false },
            { id: 'opt3', text: 'Germany', isCorrect: false },
            { id: 'opt4', text: 'Argentina', isCorrect: false }
          ],
          correctAnswer: 'France',
          explanation: 'France won the 2018 FIFA World Cup held in Russia.',
          points: 10,
          timeLimit: 30,
          tags: ['football', 'worldcup', '2018'],
          language: 'en',
          createdBy: 'admin',
          createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
          updatedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          usageCount: 2100,
          correctAnswerRate: 91.5,
          averageResponseTime: 8.7
        },
        {
          id: 'q4',
          question: 'What is the chemical symbol for gold?',
          type: QuestionType.MULTIPLE_CHOICE,
          category: QuestionCategory.SCIENCE,
          difficulty: QuestionDifficulty.MEDIUM,
          status: QuestionStatus.ACTIVE,
          options: [
            { id: 'opt1', text: 'Au', isCorrect: true },
            { id: 'opt2', text: 'Ag', isCorrect: false },
            { id: 'opt3', text: 'Go', isCorrect: false },
            { id: 'opt4', text: 'Gd', isCorrect: false }
          ],
          correctAnswer: 'Au',
          explanation: 'Au comes from the Latin word "aurum" meaning gold.',
          points: 15,
          timeLimit: 30,
          tags: ['chemistry', 'elements', 'symbols'],
          language: 'en',
          createdBy: 'admin',
          createdAt: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString(),
          updatedAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
          usageCount: 1560,
          correctAnswerRate: 68.4,
          averageResponseTime: 15.2
        },
        {
          id: 'q5',
          question: 'The Earth is flat.',
          type: QuestionType.TRUE_FALSE,
          category: QuestionCategory.SCIENCE,
          difficulty: QuestionDifficulty.EASY,
          status: QuestionStatus.ACTIVE,
          options: [
            { id: 'opt1', text: 'True', isCorrect: false },
            { id: 'opt2', text: 'False', isCorrect: true }
          ],
          correctAnswer: 'False',
          explanation: 'The Earth is an oblate spheroid, not flat.',
          points: 5,
          timeLimit: 15,
          tags: ['earth', 'shape', 'basic'],
          language: 'en',
          createdBy: 'admin',
          createdAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
          updatedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
          usageCount: 3200,
          correctAnswerRate: 96.8,
          averageResponseTime: 5.1
        }
      ],
      categories: [
        {
          category: QuestionCategory.GENERAL_KNOWLEDGE,
          name: 'General Knowledge',
          description: 'Broad range of general knowledge questions',
          icon: '🧠',
          color: '#007bff',
          totalQuestions: 450,
          activeQuestions: 420,
          averageDifficulty: 2.1,
          popularityScore: 8.5,
          lastUpdated: new Date().toISOString()
        },
        {
          category: QuestionCategory.SPORTS,
          name: 'Sports',
          description: 'Questions about various sports and athletes',
          icon: '⚽',
          color: '#28a745',
          totalQuestions: 320,
          activeQuestions: 305,
          averageDifficulty: 1.8,
          popularityScore: 7.2,
          lastUpdated: new Date().toISOString()
        },
        {
          category: QuestionCategory.TECHNOLOGY,
          name: 'Technology',
          description: 'Programming, computers, and tech questions',
          icon: '💻',
          color: '#6f42c1',
          totalQuestions: 280,
          activeQuestions: 265,
          averageDifficulty: 2.4,
          popularityScore: 6.8,
          lastUpdated: new Date().toISOString()
        },
        {
          category: QuestionCategory.SCIENCE,
          name: 'Science',
          description: 'Physics, chemistry, biology, and more',
          icon: '🔬',
          color: '#20c997',
          totalQuestions: 380,
          activeQuestions: 360,
          averageDifficulty: 2.3,
          popularityScore: 7.9,
          lastUpdated: new Date().toISOString()
        },
        {
          category: QuestionCategory.GEOGRAPHY,
          name: 'Geography',
          description: 'Countries, capitals, landmarks, and maps',
          icon: '🌍',
          color: '#17a2b8',
          totalQuestions: 290,
          activeQuestions: 275,
          averageDifficulty: 1.9,
          popularityScore: 6.5,
          lastUpdated: new Date().toISOString()
        }
      ],
      difficulties: [
        {
          difficulty: QuestionDifficulty.EASY,
          name: 'Easy',
          description: 'Basic questions suitable for beginners',
          pointsMultiplier: 1.0,
          timeMultiplier: 1.0,
          color: '#28a745',
          icon: '🟢',
          minPoints: 5,
          maxPoints: 15,
          defaultTimeLimit: 30
        },
        {
          difficulty: QuestionDifficulty.MEDIUM,
          name: 'Medium',
          description: 'Moderate difficulty questions',
          pointsMultiplier: 1.5,
          timeMultiplier: 1.2,
          color: '#ffc107',
          icon: '🟡',
          minPoints: 10,
          maxPoints: 25,
          defaultTimeLimit: 45
        },
        {
          difficulty: QuestionDifficulty.HARD,
          name: 'Hard',
          description: 'Challenging questions for experts',
          pointsMultiplier: 2.0,
          timeMultiplier: 1.5,
          color: '#dc3545',
          icon: '🔴',
          minPoints: 20,
          maxPoints: 50,
          defaultTimeLimit: 60
        }
      ],
      templates: [],
      analytics: {
        totalQuestions: 1720,
        activeQuestions: 1625,
        questionsByCategory: {
          [QuestionCategory.GENERAL_KNOWLEDGE]: 450,
          [QuestionCategory.SPORTS]: 320,
          [QuestionCategory.TECHNOLOGY]: 280,
          [QuestionCategory.SCIENCE]: 380,
          [QuestionCategory.GEOGRAPHY]: 290,
          [QuestionCategory.HISTORY]: 0,
          [QuestionCategory.ENTERTAINMENT]: 0,
          [QuestionCategory.LITERATURE]: 0,
          [QuestionCategory.MATHEMATICS]: 0,
          [QuestionCategory.POLITICS]: 0
        },
        questionsByDifficulty: {
          [QuestionDifficulty.EASY]: 860,
          [QuestionDifficulty.MEDIUM]: 580,
          [QuestionDifficulty.HARD]: 280
        },
        questionsByStatus: {
          [QuestionStatus.ACTIVE]: 1625,
          [QuestionStatus.INACTIVE]: 75,
          [QuestionStatus.PENDING_REVIEW]: 15,
          [QuestionStatus.REJECTED]: 5
        },
        averageCorrectAnswerRate: 78.5,
        averageResponseTime: 16.8,
        mostPopularCategories: [],
        leastPerformingQuestions: [],
        topPerformingQuestions: [],
        recentActivity: []
      },
      summary: {
        totalQuestions: 1720,
        activeQuestions: 1625,
        totalCategories: 10,
        totalTemplates: 0,
        activeTemplates: 0,
        averageCorrectAnswerRate: 78.5,
        questionsAddedToday: 12,
        questionsPlayedToday: 2450
      }
    };
  }

  // Dashboard Methods
  public async getDashboardOverview(): Promise<MainDashboardOverview> {
      return this.getMockDashboardDataWithPrometheus();
    
  }

  public async addQuickQuestion(request: AddQuestionRequest): Promise<any> {
    try {
      const response = await axios.post(
        `${this.config.host}/v2/console/dashboard/quick-actions/add-question`,
        request,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to add quick question:', error);
      throw error;
    }
  }

  public async sendQuickNotification(request: DashboardSendNotificationRequest): Promise<any> {
    try {
      const response = await axios.post(
        `${this.config.host}/v2/console/dashboard/quick-actions/send-notification`,
        request,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to send quick notification:', error);
      throw error;
    }
  }

  public async quickBanUser(request: DashboardBanUserRequest): Promise<any> {
    try {
      const response = await axios.post(
        `${this.config.host}/v2/console/dashboard/quick-actions/ban-user`,
        request,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to ban user:', error);
      throw error;
    }
  }

  // Mock data for development/demo purposes
  private getMockDashboardData(): MainDashboardOverview {
    return {
      userStatistics: {
        totalUsers: 15420,
        activeUsers: 8750,
        inactiveUsers: 6120,
        bannedUsers: 550,
        newUsersToday: 45,
        newUsersThisWeek: 312,
        newUsersThisMonth: 1280,
        onlineUsers: 1250,
        averageSessionTime: 28.5
      },
      gamesPlayed: {
        totalGamesPlayed: 125000,
        totalUniquePlayers: 12500,
        averageGamesPerUser: 10.0,
        gameStatistics: [
          {
            game: GameTitle.LUDO,
            totalPlays: 45000,
            uniquePlayers: 8500,
            averageSessionTime: 15.2,
            completionRate: 85.5,
            topScore: 2500,
            playsToday: 450,
            playsThisWeek: 3200,
            playsThisMonth: 12800,
            modes: [
              {
                mode: GameMode.MULTIPLAYER,
                totalPlays: 25000,
                uniquePlayers: 6000,
                averageSessionTime: 18.5,
                completionRate: 88.2
              },
              {
                mode: GameMode.VS_AI,
                totalPlays: 12000,
                uniquePlayers: 4500,
                averageSessionTime: 12.8,
                completionRate: 82.1
              },
              {
                mode: GameMode.PASS_AND_PLAY,
                totalPlays: 5000,
                uniquePlayers: 2000,
                averageSessionTime: 20.3,
                completionRate: 90.5
              },
              {
                mode: GameMode.FRIENDS,
                totalPlays: 3000,
                uniquePlayers: 1500,
                averageSessionTime: 22.1,
                completionRate: 92.8
              }
            ]
          },
          {
            game: GameTitle.QUIZ,
            totalPlays: 52000,
            uniquePlayers: 9200,
            averageSessionTime: 8.5,
            completionRate: 78.2,
            topScore: 15000,
            playsToday: 520,
            playsThisWeek: 3640,
            playsThisMonth: 14560,
            modes: [
              {
                mode: GameMode.SINGLE_PLAYER,
                totalPlays: 35000,
                uniquePlayers: 7500,
                averageSessionTime: 7.2,
                completionRate: 75.8
              },
              {
                mode: GameMode.MULTIPLAYER,
                totalPlays: 17000,
                uniquePlayers: 4200,
                averageSessionTime: 11.5,
                completionRate: 82.5
              }
            ]
          },
          {
            game: GameTitle.SOLITAIRE,
            totalPlays: 28000,
            uniquePlayers: 5800,
            averageSessionTime: 12.8,
            completionRate: 65.2,
            topScore: 8500,
            playsToday: 280,
            playsThisWeek: 1960,
            playsThisMonth: 7840,
            modes: [
              {
                mode: GameMode.SINGLE_PLAYER,
                totalPlays: 28000,
                uniquePlayers: 5800,
                averageSessionTime: 12.8,
                completionRate: 65.2
              }
            ]
          }
        ],
        modeDistribution: {
          singlePlayer: 63000,
          multiplayer: 62000
        }
      },
      quickActionStats: {
        questionsAdded: 1250,
        notificationsSent: 850,
        usersBanned: 125,
        actionsToday: 25
      },
      recentActivity: [
        {
          id: '1',
          type: 'user_registered',
          description: 'New user registered',
          timestamp: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
          userId: 'user123',
          username: 'player_newbie'
        },
        {
          id: '2',
          type: 'game_completed',
          description: 'Ludo game completed with high score',
          timestamp: new Date(Date.now() - 12 * 60 * 1000).toISOString(),
          userId: 'user456',
          username: 'ludo_master',
          details: { game: 'ludo', score: 2450 }
        },
        {
          id: '3',
          type: 'notification_sent',
          description: 'Push notification sent to active users',
          timestamp: new Date(Date.now() - 25 * 60 * 1000).toISOString(),
          details: { recipients: 8750, title: 'New Quiz Categories Available!' }
        },
        {
          id: '4',
          type: 'question_added',
          description: 'New quiz question added to Science category',
          timestamp: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
          details: { category: 'science', difficulty: 'medium' }
        },
        {
          id: '5',
          type: 'user_banned',
          description: 'User banned for inappropriate behavior',
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          userId: 'user789',
          username: 'troublemaker',
          details: { reason: 'Inappropriate chat messages', duration: 24 }
        },
        {
          id: '6',
          type: 'game_completed',
          description: 'Quiz game completed with perfect score',
          timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
          userId: 'user321',
          username: 'quiz_genius',
          details: { game: 'quiz', score: 15000, category: 'technology' }
        }
      ],
      systemHealth: {
        serverStatus: 'healthy',
        uptime: 2592000, // 30 days in seconds
        activeConnections: 1250,
        memoryUsage: 68.5,
        cpuUsage: 32.1
      }
    };
  }

  // Dashboard data with real user statistics from Nakama API
  private async getMockDashboardDataWithPrometheus(): Promise<MainDashboardOverview> {
    let totalUsers = 0;
    let bannedUsers = 0;
    let onlineUsers = 0;
    
    try {
      // Fetch all accounts from Nakama API to get real counts
      let cursor: string | null = null;
      let allAccounts: any[] = [];
      
      // Paginate through all accounts
      do {
        const accountsResponse: AccountList = await this.listAccounts('', undefined, false, cursor);
        if (accountsResponse.users) {
          allAccounts = allAccounts.concat(accountsResponse.users);
        }
        cursor = accountsResponse.next_cursor || null;
      } while (cursor);
      
      // Filter and count users
      for (const account of allAccounts) {
        // Skip system users (ID starting with '00000000-' or metadata.system === true)
        const isSystemUser = account.user?.id?.startsWith('00000000-') || 
                            (account.user?.metadata && JSON.parse(account.user.metadata || '{}').system === true);
        
        if (isSystemUser) continue;
        
        // Check if user is banned (disable_time is set and not empty)
        const isBanned = account.disable_time && account.disable_time !== '1970-01-01T00:00:00Z';
        
        if (isBanned) {
          bannedUsers++;
        } else {
          totalUsers++;
        }
      }
      
      console.log(`[Dashboard] Real user statistics - Total: ${totalUsers}, Banned: ${bannedUsers}`);
    } catch (error) {
      console.warn('[Dashboard] Failed to fetch accounts, using fallback:', error);
      totalUsers = 0;
      bannedUsers = 0;
    }
    
    try {
      // Get online users from status API
      const statusResponse = await this.getStatus('');
      if (statusResponse && statusResponse.nodes) {
        // Sum up session_count from all nodes (CCU - actual connected users)
        onlineUsers = statusResponse.nodes.reduce((sum: number, node: any) => {
          return sum + (node.session_count || 0);
        }, 0);
      }
      console.log(`[Dashboard] Online users (CCU): ${onlineUsers}`);
    } catch (error) {
      console.warn('[Dashboard] Failed to fetch status, online users set to 0:', error);
      onlineUsers = 0;
    }
    
    // Calculate inactive users (total - online)
    const inactiveUsers = Math.max(0, totalUsers - onlineUsers);

    return {
      userStatistics: {
        totalUsers,
        activeUsers: onlineUsers, // Active = currently online
        inactiveUsers,
        bannedUsers,
        newUsersToday: 0, // Not tracked
        newUsersThisWeek: 0, // Not tracked
        newUsersThisMonth: 0, // Not tracked
        onlineUsers,
        averageSessionTime: 0 // Not tracked
      },
      gamesPlayed: {
        totalGamesPlayed: Math.floor(totalUsers * 8.1), // ~8.1 games per user on average
        totalUniquePlayers: Math.floor(totalUsers * 0.81), // ~81% of users have played games
        averageGamesPerUser: 10.0,
        gameStatistics: [
          {
            game: GameTitle.LUDO,
            totalPlays: Math.floor(totalUsers * 2.92), // ~2.92 Ludo games per user
            uniquePlayers: Math.floor(totalUsers * 0.55), // ~55% played Ludo
            averageSessionTime: 15.2,
            completionRate: 85.5,
            topScore: 2500,
            playsToday: Math.floor(totalUsers * 0.029),
            playsThisWeek: Math.floor(totalUsers * 0.208),
            playsThisMonth: Math.floor(totalUsers * 0.83),
            modes: [
              {
                mode: GameMode.MULTIPLAYER,
                totalPlays: Math.floor(totalUsers * 1.62),
                uniquePlayers: Math.floor(totalUsers * 0.39),
                averageSessionTime: 18.5,
                completionRate: 88.2
              },
              {
                mode: GameMode.VS_AI,
                totalPlays: Math.floor(totalUsers * 0.78),
                uniquePlayers: Math.floor(totalUsers * 0.29),
                averageSessionTime: 12.8,
                completionRate: 82.1
              },
              {
                mode: GameMode.PASS_AND_PLAY,
                totalPlays: Math.floor(totalUsers * 0.32),
                uniquePlayers: Math.floor(totalUsers * 0.13),
                averageSessionTime: 20.3,
                completionRate: 90.5
              },
              {
                mode: GameMode.FRIENDS,
                totalPlays: Math.floor(totalUsers * 0.19),
                uniquePlayers: Math.floor(totalUsers * 0.097),
                averageSessionTime: 22.1,
                completionRate: 92.8
              }
            ]
          },
          {
            game: GameTitle.QUIZ,
            totalPlays: Math.floor(totalUsers * 3.37), // ~3.37 Quiz games per user
            uniquePlayers: Math.floor(totalUsers * 0.597), // ~59.7% played Quiz
            averageSessionTime: 8.5,
            completionRate: 78.2,
            topScore: 15000,
            playsToday: Math.floor(totalUsers * 0.034),
            playsThisWeek: Math.floor(totalUsers * 0.236),
            playsThisMonth: Math.floor(totalUsers * 0.944),
            modes: [
              {
                mode: GameMode.SINGLE_PLAYER,
                totalPlays: Math.floor(totalUsers * 2.27),
                uniquePlayers: Math.floor(totalUsers * 0.487),
                averageSessionTime: 7.2,
                completionRate: 75.8
              },
              {
                mode: GameMode.MULTIPLAYER,
                totalPlays: Math.floor(totalUsers * 1.10),
                uniquePlayers: Math.floor(totalUsers * 0.272),
                averageSessionTime: 11.5,
                completionRate: 82.5
              }
            ]
          },
          {
            game: GameTitle.SOLITAIRE,
            totalPlays: Math.floor(totalUsers * 1.82), // ~1.82 Solitaire games per user
            uniquePlayers: Math.floor(totalUsers * 0.376), // ~37.6% played Solitaire
            averageSessionTime: 12.8,
            completionRate: 65.2,
            topScore: 8500,
            playsToday: Math.floor(totalUsers * 0.018),
            playsThisWeek: Math.floor(totalUsers * 0.127),
            playsThisMonth: Math.floor(totalUsers * 0.508),
            modes: [
              {
                mode: GameMode.SINGLE_PLAYER,
                totalPlays: Math.floor(totalUsers * 1.82),
                uniquePlayers: Math.floor(totalUsers * 0.376),
                averageSessionTime: 12.8,
                completionRate: 65.2
              }
            ]
          }
        ],
        modeDistribution: {
          singlePlayer: Math.floor(totalUsers * 4.09), // Single player games
          multiplayer: Math.floor(totalUsers * 4.01) // Multiplayer games
        }
      },
      quickActionStats: {
        questionsAdded: 1250,
        notificationsSent: 850,
        usersBanned: bannedUsers,
        actionsToday: 45
      },
      recentActivity: [
        {
          id: '1',
          type: 'user_registered',
          description: 'New user registered from mobile app',
          timestamp: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
          userId: 'user123',
          username: 'newgamer2024',
          details: { platform: 'android', referral: 'organic' }
        },
        {
          id: '2',
          type: 'game_completed',
          description: 'Ludo multiplayer game completed',
          timestamp: new Date(Date.now() - 12 * 60 * 1000).toISOString(),
          userId: 'user456',
          username: 'ludo_master',
          details: { game: 'ludo', mode: 'multiplayer', score: 2350, duration: 18 }
        },
        {
          id: '3',
          type: 'notification_sent',
          description: 'Push notification sent to active users',
          timestamp: new Date(Date.now() - 25 * 60 * 1000).toISOString(),
          details: { recipients: totalUsers, title: 'New Quiz Categories Available!' }
        },
        {
          id: '4',
          type: 'question_added',
          description: 'New quiz question added to Science category',
          timestamp: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
          details: { category: 'science', difficulty: 'medium' }
        },
        {
          id: '5',
          type: 'user_banned',
          description: 'User banned for inappropriate behavior',
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          userId: 'user789',
          username: 'troublemaker',
          details: { reason: 'Inappropriate chat messages', duration: 24 }
        },
        {
          id: '6',
          type: 'game_completed',
          description: 'Quiz game completed with perfect score',
          timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
          userId: 'user321',
          username: 'quiz_genius',
          details: { game: 'quiz', score: 15000, category: 'technology' }
        }
      ],
      systemHealth: {
        serverStatus: 'healthy',
        uptime: 2592000, // 30 days in seconds
        activeConnections: onlineUsers,
        memoryUsage: 68.5,
        cpuUsage: 32.1
      }
    };
  }

  // Container Settings Methods
  async getContainerConfig(): Promise<ContainerConfig> {
    try {
      const response: AxiosResponse<ContainerConfigResponse> = await axios.get(
        `${this.config.host}/v2/console/container/config`,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data.data;
    } catch (error) {
      console.error('Failed to get container config:', error);
      // Return mock data for development
      return this.getMockContainerConfig();
    }
  }

  async updateContainerConfig(config: ContainerConfig): Promise<ContainerConfig> {
    try {
      const request: ContainerConfigRequest = { config };
      const response: AxiosResponse<ContainerConfigResponse> = await axios.put(
        `${this.config.host}/v2/console/container/config`,
        request,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data.data;
    } catch (error) {
      console.error('Failed to update container config:', error);
      throw error;
    }
  }

  async getContainerStats(): Promise<any> {
    try {
      const response: AxiosResponse<ContainerStatsResponse> = await axios.get(
        `${this.config.host}/v2/console/container/stats`,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data.data;
    } catch (error) {
      console.error('Failed to get container stats:', error);
      throw error;
    }
  }

  async getContainerLogs(request: ContainerLogsRequest): Promise<any[]> {
    try {
      const response: AxiosResponse<ContainerLogsResponse> = await axios.get(
        `${this.config.host}/v2/console/container/logs`,
        {
          params: request,
          headers: this.getAuthHeaders()
        }
      );

      return response.data.data;
    } catch (error) {
      console.error('Failed to get container logs:', error);
      throw error;
    }
  }

  async performContainerOperation(request: ContainerOperationRequest): Promise<any> {
    try {
      const response: AxiosResponse<ContainerOperationResponse> = await axios.post(
        `${this.config.host}/v2/console/container/operation`,
        request,
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data.data;
    } catch (error) {
      console.error('Failed to perform container operation:', error);
      throw error;
    }
  }

  // Mock data for container settings
  private getMockContainerConfig(): ContainerConfig {
    return {
      name: 'nakama-server',
      image: 'nakama:latest',
      restartPolicy: 'unless-stopped',
      autoStart: true,
      environment: [
        {
          name: 'NAKAMA_DATABASE_URL',
          value: 'postgres://nakama:password@postgres:5432/nakama',
          description: 'Database connection URL'
        },
        {
          name: 'NAKAMA_LOG_LEVEL',
          value: 'INFO',
          description: 'Logging level for the server'
        },
        {
          name: 'NAKAMA_RUNTIME_PATH',
          value: '/nakama/data/modules',
          description: 'Path to runtime modules'
        }
      ],
      resources: {
        cpuLimit: 2.0,
        memoryLimit: 2048,
        cpuReservation: 0.5,
        memoryReservation: 512
      },
      volumes: [
        {
          name: 'nakama-data',
          hostPath: '/opt/nakama/data',
          containerPath: '/nakama/data',
          readOnly: false,
          type: 'bind'
        },
        {
          name: 'nakama-logs',
          hostPath: '/var/log/nakama',
          containerPath: '/nakama/logs',
          readOnly: false,
          type: 'bind'
        }
      ],
      networking: {
        mode: 'bridge',
        hostname: 'nakama-server',
        ports: [
          {
            hostPort: 7349,
            containerPort: 7349,
            protocol: 'tcp'
          },
          {
            hostPort: 7350,
            containerPort: 7350,
            protocol: 'tcp'
          },
          {
            hostPort: 7351,
            containerPort: 7351,
            protocol: 'tcp'
          }
        ]
      },
      createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      updatedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      status: 'running'
    };
  }

  // Game Settings Management
  async updateGameSettings(gameType: string, settings: any): Promise<void> {
    // Mock implementation - in real app, this would save to backend
    await new Promise(resolve => setTimeout(resolve, 1000));
    console.log(`Updated ${gameType} settings:`, settings);
  }

  // Leaderboard Configuration Management
  async updateLeaderboardConfig(gameType: string, config: any): Promise<void> {
    // Mock implementation - in real app, this would save leaderboard config to backend
    await new Promise(resolve => setTimeout(resolve, 1000));
    console.log(`Updated ${gameType} leaderboard configuration:`, config);
  }

  // Helper function to normalize leaderboard record from API (camelCase) to expected format (snake_case)
  private normalizeLeaderboardRecord(record: any): any {
    // Parse metadata to get display_name
    let displayName = 'Anonymous';
    try {
      if (record.metadata) {
        const metadata = typeof record.metadata === 'string' ? JSON.parse(record.metadata) : record.metadata;
        displayName = metadata.display_name || metadata.displayName || 'Anonymous';
      }
    } catch (e) {
      // Ignore parse errors
    }
    
    return {
      owner_id: record.ownerId || record.owner_id,
      username: displayName,
      score: record.score,
      subscore: record.subscore || record.numScore || '0',
      num_score: record.numScore || record.num_score || 0,
      metadata: record.metadata,
      create_time: record.createTime || record.create_time,
      update_time: record.updateTime || record.update_time,
      rank: typeof record.rank === 'string' ? parseInt(record.rank) : record.rank,
      max_num_score: record.maxNumScore || record.max_num_score,
      leaderboard_id: record.leaderboardId || record.leaderboard_id,
    };
  }

  // Get specific leaderboard for Ludo game
  async getLudoLeaderboard(leaderboardId: string = 'total_earned_coins_ludo'): Promise<{ leaderboard: Leaderboard; records: LeaderboardRecordList }> {
    try {
      // Get leaderboard records using ListLeaderboardRecords endpoint (returns player names)
      const requestBody = {
        body: JSON.stringify({ leaderboardId: leaderboardId, limit: 100 }),
        user_id: '00000000-0000-0000-0000-000000000000',
        session_vars: {}
      };
      
      const recordsResponse = await axios.post<any>(
        `${this.config.host}/v2/console/api/endpoints/ListLeaderboardRecords`,
        requestBody,
        {
          headers: this.getAuthHeaders()
        }
      );
      
      // Response has body as JSON string - need to parse it
      const responseBody = typeof recordsResponse.data.body === 'string' 
        ? JSON.parse(recordsResponse.data.body) 
        : recordsResponse.data;
      
      console.log('Ludo leaderboard parsed response:', responseBody);
      
      // Normalize records from camelCase to snake_case
      const normalizedRecords = (responseBody.records || []).map((r: any) => this.normalizeLeaderboardRecord(r));
      
      console.log('Ludo leaderboard normalized records:', normalizedRecords);
      
      // Create a minimal leaderboard object (we don't need the full details)
      const leaderboard: Leaderboard = {
        id: leaderboardId,
        authoritative: false,
        sort_order: 1,
        operator: 0,
        create_time: ''
      };
      
      return { 
        leaderboard, 
        records: { 
          records: normalizedRecords,
          next_cursor: responseBody.nextCursor,
          prev_cursor: responseBody.prevCursor,
          rank_count: responseBody.rankCount
        } 
      };
    } catch (error) {
      console.error('Failed to get Ludo leaderboard:', error);
      throw error;
    }
  }

  // Get Solitaire leaderboard
  async getSolitaireLeaderboard(leaderboardId: string = 'total_earned_coins_solitaire'): Promise<{ records: any[] }> {
    try {
      // Use ListLeaderboardRecords endpoint (returns player names)
      const response = await axios.post<any>(
        `${this.config.host}/v2/console/api/endpoints/ListLeaderboardRecords`,
        {
          body: JSON.stringify({ leaderboardId: leaderboardId, limit: 100 }),
          user_id: '00000000-0000-0000-0000-000000000000',
          session_vars: {}
        },
        {
          headers: this.getAuthHeaders()
        }
      );
      
      // Response has body as JSON string - need to parse it
      const responseBody = typeof response.data.body === 'string' 
        ? JSON.parse(response.data.body) 
        : response.data;
      
      // Normalize records from camelCase to snake_case
      const normalizedRecords = (responseBody.records || []).map((r: any) => this.normalizeLeaderboardRecord(r));
      
      return { records: normalizedRecords };
    } catch (error) {
      console.error('Failed to get Solitaire leaderboard:', error);
      throw error;
    }
  }

  // Get Quiz leaderboard
  async getQuizLeaderboard(leaderboardId: string = 'total_earned_coins_quiz'): Promise<{ records: any[] }> {
    try {
      // Use ListLeaderboardRecords endpoint (returns player names)
      const response = await axios.post<any>(
        `${this.config.host}/v2/console/api/endpoints/ListLeaderboardRecords`,
        {
          body: JSON.stringify({ leaderboardId: leaderboardId, limit: 100 }),
          user_id: '00000000-0000-0000-0000-000000000000',
          session_vars: {}
        },
        {
          headers: this.getAuthHeaders()
        }
      );
      
      // Response has body as JSON string - need to parse it
      const responseBody = typeof response.data.body === 'string' 
        ? JSON.parse(response.data.body) 
        : response.data;
      
      // Normalize records from camelCase to snake_case
      const normalizedRecords = (responseBody.records || []).map((r: any) => this.normalizeLeaderboardRecord(r));
      
      return { records: normalizedRecords };
    } catch (error) {
      console.error('Failed to get Quiz leaderboard:', error);
      throw error;
    }
  }

  // Get global total earned coins leaderboard
  async getGlobalCoinsLeaderboard(limit: number = 20): Promise<LeaderboardRecordList> {
    try {
      // Use ListLeaderboardRecords endpoint (returns player names)
      const response = await axios.post<any>(
        `${this.config.host}/v2/console/api/endpoints/ListLeaderboardRecords`,
        {
          body: JSON.stringify({ leaderboardId: 'total_earned_coins_global', limit: limit }),
          user_id: '00000000-0000-0000-0000-000000000000',
          session_vars: {}
        },
        {
          headers: this.getAuthHeaders()
        }
      );
      
      // Response has body as JSON string - need to parse it
      const responseBody = typeof response.data.body === 'string' 
        ? JSON.parse(response.data.body) 
        : response.data;
      
      // Normalize records from camelCase to snake_case
      const normalizedRecords = (responseBody.records || []).map((r: any) => this.normalizeLeaderboardRecord(r));
      
      return {
        records: normalizedRecords,
        next_cursor: responseBody.nextCursor,
        prev_cursor: responseBody.prevCursor,
        rank_count: responseBody.rankCount
      };
    } catch (error) {
      console.error('Failed to get global coins leaderboard:', error);
      throw error;
    }
  }

  // Shop Configuration RPC Methods
  async getShopConfig(): Promise<any> {
    try {
      const response = await axios.post(
        `${this.config.host}/v2/console/api/endpoints/rpc/shop_config_get`,
        {},
        {
          headers: this.getAuthHeaders()
        }
      );
      
      return response.data || {};
    } catch (error) {
      console.error('Failed to get shop configuration:', error);
      throw error;
    }
  }

  async updateShopConfig(config: any): Promise<any> {
    try {
      const response = await axios.post(
        `${this.config.host}/v2/console/api/endpoints/rpc/shop_config_update`,
        config,
        {
          headers: this.getAuthHeaders()
        }
      );
      
      return response.data || {};
    } catch (error) {
      console.error('Failed to update shop configuration:', error);
      throw error;
    }
  }

  async publishShopConfig(config: any): Promise<any> {
    try {
      // Nakama console API expects { body: "json string" } format
      const payload = JSON.stringify(config);
      console.log('[publishShopConfig] Sending payload:', payload);
      
      const response = await axios.post(
        `${this.config.host}/v2/console/api/endpoints/rpc/shop_config_resetup`,
        { body: payload },
        {
          headers: this.getAuthHeaders()
        }
      );
      
      console.log('[publishShopConfig] Response:', response.data);
      return response.data || {};
    } catch (error) {
      console.error('Failed to publish shop configuration:', error);
      throw error;
    }
  }

  // Ads Configuration RPC Methods
  async getAdsConfig(): Promise<any> {
    try {
      const response = await axios.post(
        `${this.config.host}/v2/console/api/endpoints/rpc/ad_get`,
        {},
        {
          headers: this.getAuthHeaders()
        }
      );
      
      return response.data || {};
    } catch (error) {
      console.error('Failed to get ads configuration:', error);
      throw error;
    }
  }

  async updateAdsConfig(config: any): Promise<any> {
    try {
      const response = await axios.post(
        `${this.config.host}/v2/console/api/endpoints/rpc/ad_resetup`,
        {body: JSON.stringify(config)},
        {
          headers: this.getAuthHeaders()
        }
      );
      
      return response.data || {};
    } catch (error) {
      console.error('Failed to update ads configuration:', error);
      throw error;
    }
  }

  // Leaderboard RPC Methods
  async leaderboardWriteRecord(leaderboardId: string, record: {
    owner_id: string;
    username?: string;
    score?: string;
    subscore?: string;
    metadata?: string;
  }): Promise<any> {
    try {
      const response = await axios.post(
        `${this.config.host}/v2/console/api/endpoints/rpc/leaderboard_write_record`,
        {
          leaderboard_id: leaderboardId,
          owner_id: record.owner_id,
          username: record.username,
          score: record.score,
          subscore: record.subscore,
          metadata: record.metadata
        },
        {
          headers: this.getAuthHeaders()
        }
      );
      
      return response.data || {};
    } catch (error) {
      console.error('Failed to write leaderboard record:', error);
      throw error;
    }
  }

  async leaderboardReset(leaderboardId: string): Promise<any> {
    try {
      const response = await axios.post(
        `${this.config.host}/v2/console/api/endpoints/rpc/leaderboard_reset`,
        {
          leaderboard_id: leaderboardId
        },
        {
          headers: this.getAuthHeaders()
        }
      );
      
      return response.data || {};
    } catch (error) {
      console.error('Failed to reset leaderboard:', error);
      throw error;
    }
  }

  async leaderboardDeleteRecord(leaderboardId: string, ownerId: string): Promise<any> {
    try {
      const response = await axios.post(
        `${this.config.host}/v2/console/api/endpoints/rpc/leaderboard_delete_record`,
        {
          leaderboard_id: leaderboardId,
          owner_id: ownerId
        },
        {
          headers: this.getAuthHeaders()
        }
      );
      
      return response.data || {};
    } catch (error) {
      console.error('Failed to delete leaderboard record:', error);
      throw error;
    }
  }
}

export const consoleService = new ConsoleService();
export default consoleService;
